import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileBarChart, Download, Printer, Activity, ClipboardList, BellRing,
  FileText, ExternalLink, CheckCircle2, AlertTriangle, Search,
  Siren, FlaskConical, Layers, Clock, ShieldCheck, Users, PhoneCall,
  Package, Megaphone, Target, XCircle, ClipboardCheck, ScrollText,
  Filter, Smartphone, Globe, Calendar, MapPin, Tag, Zap, Gauge,
  ChevronDown, X, Check, Settings, TrendingUp, TrendingDown, BookOpen,
} from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { ApiEndPoint } from '../services/apiUttiLities';
import { isWebAdmin } from '../auth/webAccess';
import { readStoredUser } from '@/auth/saasSession';
import ExtensiveAnalysisModal from '@/components/ExtensiveAnalysisModal';
import ReportsCopilotModal from '@/components/ReportsCopilotModal';
import ReportsGuide from '../components/ReportsGuide';

// ── constants & helpers ─────────────────────────────────────────────────────
const CLOSED = ['completed', 'resolved'];
const fmt = (d) => d ? new Date(d).toLocaleString([], { year: '2-digit', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDay = (d) => d ? new Date(d).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const pct = (a, b) => b ? Math.round((a / b) * 100) : 0;

const minutesBetween = (a, b) => {
  if (!a || !b) return null;
  const diff = (new Date(b) - new Date(a)) / 60000;
  return Number.isFinite(diff) && diff >= 0 ? diff : null;
};
const ackMinutes = (i) => i?.time_to_ack_minutes ?? minutesBetween(i?.detected_at || i?.created_at, i?.acknowledged_at);
const resolveMinutes = (i) => i?.time_to_resolve_minutes ?? minutesBetween(i?.detected_at || i?.created_at, i?.resolved_at);
const fmtDur = (m) => {
  if (m == null) return '—';
  if (m < 1) return '<1m';
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${Math.round(m % 60)}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};
const median = (arr) => {
  const v = arr.filter((x) => x != null && Number.isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
};
const isDrill = (i) => i?.is_exercise === true;
const isSos = (i) => i?.created_as_sos === true;
const isOpen = (i) => !CLOSED.includes(i?.status);

const downloadCsv = (filename, rows) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};

const SEVERITY_STYLE = {
  High: { chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: '#ef4444' },
  Medium: { chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: '#f59e0b' },
  Low: { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: '#22c55e' },
};
const STATUS_STYLE = (s) => {
  if (CLOSED.includes(s)) return 'bg-gray-100 text-gray-600';
  if (s === 'active' || s === 'in_progress') return 'bg-emerald-50 text-emerald-700';
  if (s === 'open' || s === 'requested') return 'bg-amber-50 text-amber-700';
  return 'bg-sky-50 text-sky-700';
};
const PALETTE = ['#6366f1', '#f59e0b', '#22c55e', '#ef4444', '#0ea5e9', '#a855f7', '#14b8a6', '#ec4899'];

const OPS_GROUPS = [
  { key: 'notification', label: 'Notifications', icon: Megaphone },
  { key: 'acknowledgment', label: 'Acknowledgments', icon: CheckCircle2 },
  { key: 'response_action', label: 'Response actions', icon: Target },
  { key: 'resource', label: 'Resource requests', icon: Package },
  { key: 'report', label: 'Field reports', icon: FileText },
  { key: 'status_update', label: 'Status updates', icon: Activity },
  { key: 'verification', label: 'Verifications', icon: ShieldCheck },
  { key: 'call_log', label: 'Call logs', icon: PhoneCall },
];

// ── badges & chips ──────────────────────────────────────────────────────────
const KindBadge = ({ rec, size = 'xs' }) => {
  const drill = isDrill(rec);
  const cls = size === 'xs' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2.5 py-1';
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-flex items-center gap-1 font-bold rounded-full border ${cls} ${drill ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
        {drill ? <FlaskConical className="w-3 h-3" /> : <Siren className="w-3 h-3" />}
        {drill ? 'Drill' : 'Real'}
      </span>
      {isSos(rec) && <span className={`font-black rounded-full bg-red-600 text-white ${cls}`}>SOS</span>}
    </span>
  );
};

const SeverityChip = ({ severity }) => {
  const s = SEVERITY_STYLE[severity];
  if (!s) return <span className="text-gray-400">—</span>;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${s.chip}`}><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />{severity}</span>;
};

const StatusChip = ({ status }) => (
  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE(status)}`}>{String(status || '—').replace(/_/g, ' ')}</span>
);

// ── charts (CSS only, print-safe) ───────────────────────────────────────────
const BarChart = ({ data, title }) => {
  const max = Math.max(1, ...data.map(d => d.value));
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">{title}</p>
      <div className="space-y-2">
        {data.length === 0 && <p className="text-xs text-gray-400">No data</p>}
        {data.map((d, i) => (
          <div key={d.label} className="flex items-center gap-2">
            <span className="w-28 text-[11px] text-gray-600 truncate text-right capitalize">{String(d.label).replace(/_/g, ' ')}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
              <div className="h-4 rounded-full flex items-center justify-end pr-1.5 text-[9px] font-bold text-white transition-all"
                style={{ width: `${pct(d.value, max)}%`, minWidth: d.value ? '20px' : 0, backgroundColor: d.color || PALETTE[i % PALETTE.length] }}>
                {d.value || ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Donut = ({ data, title }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  let acc = 0;
  const stops = data.map((d, i) => {
    const start = pct(acc, total); acc += d.value; const end = pct(acc, total);
    return `${d.color || PALETTE[i % PALETTE.length]} ${start}% ${end}%`;
  }).join(', ');
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">{title}</p>
      <div className="flex items-center gap-4">
        <div className="w-28 h-28 rounded-full shrink-0" style={{ background: total ? `conic-gradient(${stops})` : '#e5e7eb' }}>
          <div className="w-16 h-16 bg-white rounded-full m-6 flex items-center justify-center text-sm font-black text-gray-700">{total}</div>
        </div>
        <div className="space-y-1">
          {data.map((d, i) => (
            <div key={d.label} className="flex items-center gap-1.5 text-[11px]">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: d.color || PALETTE[i % PALETTE.length] }} />
              <span className="text-gray-600 capitalize">{String(d.label).replace(/_/g, ' ')}</span>
              <span className="font-bold text-gray-800">{d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TrendChart = ({ data, title }) => {
  const max = Math.max(1, ...data.map(d => d.real + d.drill));
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">{title}</p>
        <div className="flex items-center gap-3 text-[10px] font-semibold text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-500" />Real</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-indigo-400" />Drill</span>
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-28">
        {data.map((d) => (
          <div key={d.label} title={`${d.label} — ${d.real} real · ${d.drill} drill`} className="flex-1 min-w-0 flex flex-col justify-end">
            <div className="bg-rose-500 rounded-t-sm" style={{ height: `${(d.real / max) * 100}%`, minHeight: d.real ? 3 : 0 }} />
            <div className={`bg-indigo-400 ${d.real ? '' : 'rounded-t-sm'}`} style={{ height: `${(d.drill / max) * 100}%`, minHeight: d.drill ? 3 : 0 }} />
            <div className="h-px bg-gray-200 mt-px" />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] text-gray-400 font-semibold">
        <span>{data[0]?.label}</span>
        <span>{data[Math.floor(data.length / 2)]?.label}</span>
        <span>{data[data.length - 1]?.label}</span>
      </div>
    </div>
  );
};

// Compact weekly-rate trend: one bar per week, height = compliance rate.
const MiniTrend = ({ weeks, color = 'bg-indigo-400' }) => (
  <div className="flex items-end gap-[2px] h-8 mt-2">
    {(weeks || []).map((w) => (
      <div key={w.week} className="flex-1 min-w-0 flex flex-col justify-end" title={`Week of ${w.week}: ${w.rate == null ? 'no data' : `${w.rate}%`}${w.tracked != null ? ` (${w.met}/${w.tracked})` : w.total != null ? ` (${w.responded}/${w.total})` : ''}`}>
        <div className={`${w.rate == null ? 'bg-gray-100' : color} rounded-t-sm`} style={{ height: `${w.rate == null ? 8 : Math.max(8, w.rate * 0.32 * 100 / 100)}px` }} />
      </div>
    ))}
  </div>
);

const rateTone = (rate) => rate == null ? 'text-gray-300' : rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-rose-600';

// Period-over-period stat: value now, value before, and a signed delta whose
// color depends on which direction is good.
const DeltaStat = ({ label, cur, prev, fmtFn = (v) => (v == null ? '—' : v), goodWhenDown = false }) => {
  const hasBoth = cur != null && prev != null;
  const diff = hasBoth ? cur - prev : null;
  const isGood = diff == null || diff === 0 ? null : (goodWhenDown ? diff < 0 : diff > 0);
  return (
    <div className="flex-1 min-w-[120px]">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className="text-xl font-black text-gray-900">{fmtFn(cur)}</p>
      <p className="flex items-center gap-1 text-[11px] font-bold">
        {diff == null || diff === 0
          ? <span className="text-gray-400">— unchanged</span>
          : <>
            {diff > 0 ? <TrendingUp className={`w-3 h-3 ${isGood ? 'text-emerald-500' : 'text-rose-500'}`} /> : <TrendingDown className={`w-3 h-3 ${isGood ? 'text-emerald-500' : 'text-rose-500'}`} />}
            <span className={isGood ? 'text-emerald-600' : 'text-rose-600'}>{diff > 0 ? '+' : '−'}{fmtFn(Math.abs(diff))}</span>
            <span className="text-gray-400 font-semibold">vs {fmtFn(prev)}</span>
          </>}
      </p>
    </div>
  );
};

const Kpi = ({ label, value, tone = 'text-gray-900', sub, icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 relative overflow-hidden">
    {Icon && <Icon className="w-9 h-9 absolute -right-1 -bottom-1 text-gray-100" />}
    <p className={`text-3xl font-black ${tone}`}>{value}</p>
    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mt-1">{label}</p>
    {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const Section = ({ icon: Icon, title, right, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400"><Icon className="w-3.5 h-3.5" />{title}</p>
      {right}
    </div>
    {children}
  </div>
);

// ── scope & tab configuration ───────────────────────────────────────────────
const SCOPES = [
  { id: 'all', label: 'All records', icon: Layers, active: 'bg-gray-900 text-white' },
  { id: 'real', label: 'Real incidents', icon: Siren, active: 'bg-rose-600 text-white' },
  { id: 'drill', label: 'Exercises & drills', icon: FlaskConical, active: 'bg-indigo-600 text-white' },
  { id: 'sos', label: 'SOS-origin', icon: BellRing, active: 'bg-red-600 text-white' },
];
const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'register', label: 'Incident & Exercise Register', icon: ClipboardList },
  { id: 'report', label: 'Response Report', icon: FileText },
  { id: 'perf', label: 'SLA & Performance', icon: Gauge },
  { id: 'adherence', label: 'Scenarios & Program', icon: Target },
  { id: 'ops', label: 'Operations', icon: Package },
  { id: 'acks', label: 'Acknowledgment Audit', icon: ShieldCheck },
  { id: 'ca', label: 'Corrective Actions', icon: ClipboardCheck },
  { id: 'audit', label: 'Audit Log', icon: ScrollText },
  { id: 'notifications', label: 'Notification Log', icon: BellRing },
];
const REPORT_VIEW_GROUPS = [
  { label: 'Summary & reporting', ids: ['overview', 'register', 'report'] },
  { label: 'Performance & operations', ids: ['perf', 'adherence', 'ops', 'acks'] },
  { label: 'Governance & history', ids: ['ca', 'audit', 'notifications'] },
];
const CA_STATUS_STYLE = {
  open: 'bg-slate-100 text-slate-700 border-slate-300',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-300',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
};
const CA_PRIORITY_STYLE = {
  low: 'bg-sky-50 text-sky-700 border-sky-200',
  medium: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  high: 'bg-rose-50 text-rose-700 border-rose-200',
};
const caIsOverdue = (a) => a.status !== 'completed' && a.due_date && new Date(a.due_date) < new Date();
const ALL_COLS = ['ID', 'Title', 'Kind', 'Type', 'Severity', 'Status', 'Detected', 'Time to Ack', 'Time to Resolve', 'Schedule', 'CAs'];
const DEFAULT_COLS = ALL_COLS.filter(c => c !== 'Schedule');
const REPORT_SOURCE_LABELS = {
  incidents: 'incident and exercise records',
  scenarios: 'scenario library summary',
  actions: 'corrective actions',
  notifications: 'notification audit trail',
};

// Invoke a CommanServices function that may be absent in restricted builds.
const call = (name, ...args) => {
  const fn = CommanServices[name];
  if (typeof fn !== 'function') return Promise.reject(new Error(`${name} unavailable`));
  try { return Promise.resolve(fn(...args)); } catch (e) { return Promise.reject(e); }
};

// Normalize one acknowledgment timeline/audit item into a display row.
// Works for both the per-incident timeline items and the server audit rows
// (which additionally carry incident_* fields from the join).
const buildAckRow = (a) => {
  const meta = a.meta || {};
  const sla = meta.sla || null;
  const ackAt = meta.acknowledgedAt || null;
  const actual = sla?.actualMinutes ?? minutesBetween(meta.notifiedAt, ackAt);
  // When the monitor never stamped sla.met, derive it from target vs actual.
  const met = sla?.met ?? (sla?.withinMinutes != null && actual != null ? actual <= sla.withinMinutes : null);
  return {
    id: a.id,
    incidentId: a.incident_id,
    incidentTitle: a.incident_title,
    incidentIsExercise: a.incident_is_exercise === true,
    incidentIsSos: a.incident_created_as_sos === true,
    name: meta.acknowledgedBy?.userName || a.actor?.name || 'Unknown',
    unit: meta.crew_name || meta.team_name || meta.department_name || meta.acknowledgedBy?.crewName || meta.acknowledgedBy?.teamName || '—',
    notifiedAt: meta.notifiedAt || a.timestamp,
    ackAt,
    slaTarget: sla?.withinMinutes ?? null,
    actual,
    met,
    escalated: meta.isEscalated === true,
    offDuty: meta.offDuty === true,
  };
};

// Borderless control that lives inside a FilterField container.
export const FILTER_INPUT_CLS = 'block w-full text-sm font-semibold text-gray-900 bg-transparent outline-none border-0 p-0 mt-0.5 pr-5 appearance-none cursor-pointer truncate disabled:text-gray-300 disabled:cursor-not-allowed';

// Outlined field card: the label lives inside the field, the container carries
// the focus ring, and an active filter tints the whole field indigo.
export const FilterField = ({ icon: Icon, label, active, muted, chevron = true, children, testId }) => (
  <div
    data-testid={testId}
    className={`relative flex-1 min-w-[140px] rounded-xl border px-3 pt-1.5 pb-2 transition-all focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 ${
      muted ? 'border-gray-100 bg-gray-50/70'
        : active ? 'border-indigo-300 bg-indigo-50/40 ring-1 ring-indigo-100 shadow-sm'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
    }`}
  >
    <label className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-widest ${muted ? 'text-gray-300' : active ? 'text-indigo-500' : 'text-gray-400'}`}>
      <Icon className="w-3 h-3" />{label}
      {active && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 ml-auto animate-pulse" />}
    </label>
    {children}
    {chevron && !muted && <ChevronDown className={`w-3.5 h-3.5 absolute right-2.5 bottom-2.5 pointer-events-none ${active ? 'text-indigo-400' : 'text-gray-300'}`} />}
  </div>
);

export const FilterGroup = ({ title, hint, dot, children, grow = 1 }) => (
  <div className="min-w-[250px]" style={{ flexGrow: grow, flexBasis: 0 }}>
    <p className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot || 'bg-indigo-400'}`} />{title}
      {hint && <span className="text-gray-300 font-bold normal-case tracking-normal">· {hint}</span>}
    </p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

// Searchable dropdown for high-cardinality filters: type-to-filter input,
// keyboard navigation (↑ ↓ Enter Esc), counts, and a check on the selection.
export const SearchSelect = ({ value, onChange, options, allLabel, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const shown = needle
      ? options.filter(o => `${o.label ?? o.value}`.toLowerCase().includes(needle))
      : options;
    return [
      { value: 'all', label: allLabel, count: null },
      ...shown.map(o => ({ value: o.value, label: String(o.label ?? o.value), count: o.count ?? null, badge: o.badge, section: o.section })),
    ];
  }, [options, q, allLabel]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => {
    if (open) { setQ(''); setHi(0); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [open]);
  useEffect(() => { setHi(0); }, [q]);

  const select = (v) => { onChange(v); setOpen(false); };
  const onKeyDown = (e) => {
    const k = e.key;
    if (k === 'ArrowDown' || k === 'Down') { e.preventDefault(); setHi(h => Math.min(h + 1, items.length - 1)); }
    else if (k === 'ArrowUp' || k === 'Up') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (k === 'Enter' || k === 'Return') { e.preventDefault(); if (items[hi]) select(items[hi].value); }
    else if (k === 'Escape' || k === 'Esc') { setOpen(false); }
  };

  const selected = value !== 'all' ? options.find(o => o.value === value) : null;
  const selectedText = value === 'all'
    ? allLabel
    : `${selected?.label ?? value}${selected?.count != null ? ` (${selected.count})` : ''}`;
  return (
    <div ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="block w-full text-left text-sm font-semibold text-gray-900 bg-transparent outline-none p-0 mt-0.5 pr-5 truncate cursor-pointer disabled:text-gray-300 disabled:cursor-not-allowed"
      >
        {selectedText}
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1.5 min-w-[230px] rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
          <div className="relative border-b border-gray-100 bg-gray-50/60">
            <Search className="w-3.5 h-3.5 text-indigo-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              ref={inputRef}
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type to search…"
              className="w-full pl-9 pr-3 py-2 text-sm text-gray-900 bg-transparent outline-none placeholder:text-gray-400"
            />
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto p-1">
            {items.map((it, i) => (
              <React.Fragment key={`${it.value}`}>
                {it.section && it.section !== items[i - 1]?.section && (
                  <li className="px-2.5 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-gray-300" aria-hidden="true">{it.section}</li>
                )}
                <li role="option" aria-selected={it.value === value}>
                  <button
                    type="button"
                    onClick={() => select(it.value)}
                    onMouseEnter={() => setHi(i)}
                    className={`w-full flex items-center justify-between gap-2 text-left text-sm px-2.5 py-1.5 rounded-lg transition-colors ${i === hi ? 'bg-indigo-50 text-indigo-800' : 'text-gray-700'} ${it.value === value ? 'font-bold' : ''}`}
                  >
                    <span className="truncate">{it.label}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {it.badge === 'real' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700">Real</span>}
                      {it.badge === 'drill' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700">Drill</span>}
                      {it.count != null && <span className={`text-[10px] font-bold rounded-full px-1.5 ${i === hi ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}>{it.count}</span>}
                      {it.value === value && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                    </span>
                  </button>
                </li>
              </React.Fragment>
            ))}
            {items.length === 1 && q.trim() && (
              <li className="px-2.5 py-2 text-xs text-gray-400">No matches for “{q.trim()}”</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const AckResult = ({ r }) => (
  r.met === true ? <span className="flex items-center gap-1 text-emerald-600 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5" />SLA met</span>
    : r.met === false ? <span className="flex items-center gap-1 text-rose-600 text-xs font-bold"><XCircle className="w-3.5 h-3.5" />SLA missed{r.escalated ? ' · escalated' : ''}</span>
    : r.ackAt ? <span className="text-gray-500 text-xs font-semibold">No SLA</span>
    : <span className="flex items-center gap-1 text-amber-600 text-xs font-bold"><AlertTriangle className="w-3.5 h-3.5" />Awaiting</span>
);

const ReportsCenter = ({ onCopilotVisibilityChange }) => {
  const canManageCorrectiveActions = isWebAdmin(readStoredUser());
  const [tab, setTab] = useState('overview');
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef(null);
  const viewMenuToggleRef = useRef(null);
  const reportViewRefs = useRef({});
  const [scope, setScope] = useState('all');
  const [incidents, setIncidents] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [actions, setActions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [sourceErrors, setSourceErrors] = useState([]);

  // smart filters — type › event › trigger cascade, location, severity,
  // status ('all' | 'open' | 'closed' | 's:<exact status>')
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [typeF, setTypeF] = useState('all');
  const [eventF, setEventF] = useState('all');
  const [triggerF, setTriggerF] = useState('all');
  const [locationF, setLocationF] = useState('all');
  const [severityF, setSeverityF] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [customMode, setCustomMode] = useState(false);
  const [cols, setCols] = useState(DEFAULT_COLS);

  // server-side summary (full-collection aggregation)
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(false);

  // acknowledgment audit state
  const [ackAudit, setAckAudit] = useState({ items: [], total: 0, loading: false, error: false });
  const [ackSla, setAckSla] = useState('all');

  // corrective-actions register state (tab-local controls)
  const [caStatusF, setCaStatusF] = useState('all');
  const [caOverdueOnly, setCaOverdueOnly] = useState(false);

  // audit-log state (web + mobile app trail)
  const [auditLog, setAuditLog] = useState({ items: [], total: 0, loading: false, error: false });
  const [auditSource, setAuditSource] = useState('all');
  const [auditQ, setAuditQ] = useState('');
  useEffect(() => { const t = setTimeout(() => setAuditQ(search.trim()), 400); return () => clearTimeout(t); }, [search]);

  // response report state
  const [reportId, setReportId] = useState('');
  const [reportDetail, setReportDetail] = useState(null);
  const [reportTimeline, setReportTimeline] = useState([]);
  const [reportTimelineError, setReportTimelineError] = useState(false);
  const [readinessChecks, setReadinessChecks] = useState(null); // null = unavailable/hidden
  const [showExtensive, setShowExtensive] = useState(false);
  const [showReportsCopilot, setShowReportsCopilot] = useState(true);

  useLayoutEffect(() => {
    onCopilotVisibilityChange?.(showReportsCopilot);
    return () => onCopilotVisibilityChange?.(false);
  }, [onCopilotVisibilityChange, showReportsCopilot]);

  useEffect(() => {
    if (!viewMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!viewMenuRef.current?.contains(event.target)) setViewMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setViewMenuOpen(false);
      viewMenuToggleRef.current?.focus();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    reportViewRefs.current[tab]?.focus();
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewMenuOpen, tab]);

  const fetchAll = useCallback(async () => {
    try {
      const sources = [
        ['incidents', 'getIncidentsData'],
        ['scenarios', 'listScenarios'],
        ['actions', 'listCorrectiveActions'],
        ['notifications', 'getNotifications'],
      ];
      const settled = await Promise.allSettled(sources.map(([, name]) => call(name)));
      const responses = {};
      const unavailable = [];
      settled.forEach((result, index) => {
        const source = sources[index][0];
        if (result.status !== 'fulfilled' || result.value?.status === false) {
          unavailable.push(source);
          return;
        }
        responses[source] = result.value;
      });

      if (responses.incidents) {
        const value = responses.incidents;
        setIncidents(Array.isArray(value) ? value : (Array.isArray(value?.data) ? value.data : (value?.data?.incidents || [])));
      }
      if (responses.scenarios) {
        const value = responses.scenarios;
        setScenarios(value?.data?.scenarios || value?.scenarios || (Array.isArray(value?.data) ? value.data : []));
      }
      if (responses.actions) {
        const value = responses.actions;
        setActions(Array.isArray(value) ? value : (Array.isArray(value?.data) ? value.data : []));
      }
      if (responses.notifications) {
        const value = responses.notifications;
        setNotifications(Array.isArray(value) ? value : (Array.isArray(value?.data) ? value.data : []));
      }
      setSourceErrors(unavailable);
      if (Object.keys(responses).length) setLastSync(new Date());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Realtime: every tab refreshes on a 15s cadence — no manual refresh needed.
  // The server summary re-pulls automatically because it keys on lastSync.
  useEffect(() => {
    const i = setInterval(fetchAll, 15000);
    return () => clearInterval(i);
  }, [fetchAll]);

  // Server-side summary: full-collection counts per scope + filters. Falls
  // back to client-side aggregation over the loaded page when unavailable.
  const summaryQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('scope', scope);
    if (from) p.set('from_time', from);
    if (to) p.set('to_time', `${to}T23:59:59`);
    if (typeF !== 'all') p.set('incident_type', typeF);
    if (eventF !== 'all') p.set('event', eventF);
    if (triggerF !== 'all') p.set('trigger', triggerF);
    if (locationF !== 'all') p.set('location', locationF);
    if (severityF !== 'all') p.set('severity', severityF);
    if (statusF.startsWith('s:')) p.set('status', statusF.slice(2));
    else if (statusF !== 'all') p.set('status_mode', statusF);
    if (auditQ) p.set('q', auditQ);
    return p.toString();
  }, [scope, from, to, typeF, eventF, triggerF, locationF, severityF, statusF, auditQ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await call('getReportsSummary', summaryQuery);
        if (cancelled) return;
        if (r?.status === false) throw new Error(r?.message || 'summary unavailable');
        const d = r?.data ?? r;
        if (d?.filtered) { setSummary(d); setSummaryError(false); }
        else throw new Error('malformed summary');
      } catch {
        if (!cancelled) { setSummary(null); setSummaryError(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [summaryQuery, lastSync]);

  // Acknowledgment audit: server-paginated, honoring every smart filter.
  const ackQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('scope', scope);
    if (ackSla !== 'all') p.set('sla', ackSla);
    if (from) p.set('from_time', from);
    if (to) p.set('to_time', `${to}T23:59:59`);
    if (typeF !== 'all') p.set('incident_type', typeF);
    if (eventF !== 'all') p.set('event', eventF);
    if (triggerF !== 'all') p.set('trigger', triggerF);
    if (locationF !== 'all') p.set('location', locationF);
    if (severityF !== 'all') p.set('severity', severityF);
    if (statusF.startsWith('s:')) p.set('status', statusF.slice(2));
    else if (statusF !== 'all') p.set('status_mode', statusF);
    if (auditQ) p.set('q', auditQ);
    return p;
  }, [scope, ackSla, from, to, typeF, eventF, triggerF, locationF, severityF, statusF, auditQ]);

  const ackAuditRef = React.useRef(null);
  const loadAckAudit = useCallback(async (append) => {
    setAckAudit(prev => ({ ...prev, loading: true, error: false, items: append ? prev.items : [] }));
    try {
      const p = new URLSearchParams(ackQuery);
      p.set('limit', '50');
      p.set('skip', append ? String(ackAuditRef.current?.items?.length || 0) : '0');
      const r = await call('getReportsAcknowledgments', p.toString());
      if (r?.status === false) throw new Error(r?.message || 'audit unavailable');
      const d = r?.data ?? r;
      const rows = (d?.items || []).map(buildAckRow);
      setAckAudit(prev => ({
        items: append ? [...prev.items, ...rows] : rows,
        total: d?.total ?? rows.length,
        loading: false,
        error: false,
      }));
    } catch {
      setAckAudit(prev => ({ ...prev, loading: false, error: true }));
    }
  }, [ackQuery]);
  useEffect(() => { ackAuditRef.current = ackAudit; }, [ackAudit]);
  useEffect(() => { if (tab === 'acks') loadAckAudit(false); }, [tab, loadAckAudit]);
  // Keep the audit live too — but don't clobber a "load more" expansion.
  useEffect(() => {
    if (tab !== 'acks') return;
    const i = setInterval(() => {
      const current = ackAuditRef.current;
      if (!current?.loading && (current?.items?.length || 0) <= 50) loadAckAudit(false);
    }, 30000);
    return () => clearInterval(i);
  }, [tab, loadAckAudit]);

  // Full audit log (web console + mobile app): server-paginated and live.
  const auditRef = React.useRef(null);
  const auditQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('scope', scope);
    if (auditSource !== 'all') p.set('source', auditSource);
    if (from) p.set('from_time', from);
    if (to) p.set('to_time', `${to}T23:59:59`);
    if (typeF !== 'all') p.set('incident_type', typeF);
    if (eventF !== 'all') p.set('event', eventF);
    if (triggerF !== 'all') p.set('trigger', triggerF);
    if (locationF !== 'all') p.set('location', locationF);
    if (severityF !== 'all') p.set('severity', severityF);
    if (statusF.startsWith('s:')) p.set('status', statusF.slice(2));
    else if (statusF !== 'all') p.set('status_mode', statusF);
    if (auditQ) p.set('q', auditQ);
    return p;
  }, [scope, auditSource, from, to, typeF, eventF, triggerF, locationF, severityF, statusF, auditQ]);
  const loadAuditLog = useCallback(async (append) => {
    setAuditLog(prev => ({ ...prev, loading: true, error: false, items: append ? prev.items : [] }));
    try {
      const p = new URLSearchParams(auditQuery);
      p.set('limit', '50');
      p.set('skip', append ? String(auditRef.current?.items?.length || 0) : '0');
      const r = await call('getReportsAuditLog', p.toString());
      if (r?.status === false) throw new Error(r?.message || 'audit log unavailable');
      const d = r?.data ?? r;
      const rows = d?.items || [];
      setAuditLog(prev => ({
        items: append ? [...prev.items, ...rows] : rows,
        total: d?.total ?? rows.length,
        loading: false,
        error: false,
      }));
    } catch {
      setAuditLog(prev => ({ ...prev, loading: false, error: true }));
    }
  }, [auditQuery]);
  useEffect(() => { auditRef.current = auditLog; }, [auditLog]);
  useEffect(() => { if (tab === 'audit') loadAuditLog(false); }, [tab, loadAuditLog]);
  useEffect(() => {
    if (tab !== 'audit') return;
    const i = setInterval(() => {
      const current = auditRef.current;
      if (!current?.loading && (current?.items?.length || 0) <= 50) loadAuditLog(false);
    }, 30000);
    return () => clearInterval(i);
  }, [tab, loadAuditLog]);

  // SLA & Performance + Scenario Adherence analytics: same filter language as
  // every other tab, refreshed while their tab is open.
  const dimsQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('scope', scope);
    if (from) p.set('from_time', from);
    if (to) p.set('to_time', `${to}T23:59:59`);
    if (typeF !== 'all') p.set('incident_type', typeF);
    if (eventF !== 'all') p.set('event', eventF);
    if (triggerF !== 'all') p.set('trigger', triggerF);
    if (locationF !== 'all') p.set('location', locationF);
    if (severityF !== 'all') p.set('severity', severityF);
    if (statusF.startsWith('s:')) p.set('status', statusF.slice(2));
    else if (statusF !== 'all') p.set('status_mode', statusF);
    if (auditQ) p.set('q', auditQ);
    return p.toString();
  }, [scope, from, to, typeF, eventF, triggerF, locationF, severityF, statusF, auditQ]);

  const [perf, setPerf] = useState({ data: null, loading: false, error: false });
  const [adherence, setAdherence] = useState({ data: null, loading: false, error: false });
  const [opsData, setOpsData] = useState({ data: null, loading: false, error: false });
  const [registerData, setRegisterData] = useState({ items: [], total: 0, loading: false, error: false });
  const registerRef = useRef(null);
  const [program, setProgram] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [benchOpen, setBenchOpen] = useState(false);
  const [benchDraft, setBenchDraft] = useState({});
  const [digest, setDigest] = useState(null);
  const [compare, setCompare] = useState(null);
  const [reportComms, setReportComms] = useState(null);
  const [savedViews, setSavedViews] = useState(() => {
    try { return JSON.parse(localStorage.getItem('reportsSavedViews') || '[]'); } catch { return []; }
  });
  const [showGuide, setShowGuide] = useState(false);
  useEffect(() => {
    if (tab !== 'perf') return;
    let cancelled = false;
    const load = async () => {
      setPerf(p => ({ ...p, loading: true }));
      try {
        const r = await call('getReportsPerformance', dimsQuery);
        if (cancelled) return;
        if (r?.status === false) throw new Error(r?.message || 'unavailable');
        const d = r?.data ?? r;
        if (!d?.sla) throw new Error('malformed');
        setPerf({ data: d, loading: false, error: false });
      } catch {
        if (!cancelled) setPerf(p => ({ ...p, loading: false, error: true }));
      }
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [tab, dimsQuery]);
  useEffect(() => {
    if (tab !== 'adherence') return;
    let cancelled = false;
    const load = async () => {
      setAdherence(p => ({ ...p, loading: true }));
      try {
        const r = await call('getReportsAdherence', dimsQuery);
        if (cancelled) return;
        if (r?.status === false) throw new Error(r?.message || 'unavailable');
        const d = r?.data ?? r;
        if (!d?.funnel) throw new Error('malformed');
        setAdherence({ data: d, loading: false, error: false });
      } catch {
        if (!cancelled) setAdherence(p => ({ ...p, loading: false, error: true }));
      }
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [tab, dimsQuery]);

  // Server-paginated register: filtering happens in the query over the full
  // collection, so the table stays complete past the browser's 1,000-record
  // incident page. Falls back to the client-side list when unavailable.
  const loadRegister = useCallback(async (append) => {
    setRegisterData(prev => ({ ...prev, loading: true, error: false, items: append ? prev.items : prev.items }));
    try {
      const p = new URLSearchParams(dimsQuery);
      p.set('limit', '100');
      p.set('skip', append ? String(registerRef.current?.items?.length || 0) : '0');
      const r = await call('getReportsRegister', p.toString());
      if (r?.status === false) throw new Error(r?.message || 'register unavailable');
      const d = r?.data ?? r;
      if (!Array.isArray(d?.items)) throw new Error('malformed');
      setRegisterData(prev => ({
        items: append ? [...prev.items, ...d.items] : d.items,
        total: d.total ?? d.items.length,
        loading: false,
        error: false,
      }));
    } catch {
      setRegisterData(prev => ({ ...prev, loading: false, error: true }));
    }
  }, [dimsQuery]);
  useEffect(() => { registerRef.current = registerData; }, [registerData]);
  useEffect(() => { if (tab === 'register') loadRegister(false); }, [tab, loadRegister]);
  useEffect(() => {
    if (tab !== 'register') return;
    const i = setInterval(() => {
      const current = registerRef.current;
      if (!current?.loading && (current?.items?.length || 0) <= 100) loadRegister(false);
    }, 30000);
    return () => clearInterval(i);
  }, [tab, loadRegister]);

  // Operations analytics (resources, action cards, calls).
  useEffect(() => {
    if (tab !== 'ops') return;
    let cancelled = false;
    const load = async () => {
      setOpsData(p => ({ ...p, loading: true }));
      try {
        const r = await call('getReportsOperations', dimsQuery);
        if (cancelled) return;
        if (r?.status === false) throw new Error(r?.message || 'unavailable');
        const d = r?.data ?? r;
        if (!d?.resources) throw new Error('malformed');
        setOpsData({ data: d, loading: false, error: false });
      } catch {
        if (!cancelled) setOpsData(p => ({ ...p, loading: false, error: true }));
      }
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [tab, dimsQuery]);

  // Exercise program compliance (institution-wide; loads with the tab).
  useEffect(() => {
    if (tab !== 'adherence') return;
    let cancelled = false;
    (async () => {
      const r = await call('getReportsProgram').catch(() => null);
      if (cancelled) return;
      const d = r?.status === false ? null : (r?.data ?? r);
      if (d?.cadence) setProgram(d);
    })();
    return () => { cancelled = true; };
  }, [tab, lastSync]);

  // Benchmarks + digest schedule (once, refreshed after saves).
  useEffect(() => {
    (async () => {
      const [b, s] = await Promise.allSettled([call('getReportsBenchmarks'), call('getReportsSchedule')]);
      if (b.status === 'fulfilled' && b.value?.status !== false) setBenchmarks(b.value?.data ?? b.value);
      if (s.status === 'fulfilled' && s.value?.status !== false) setDigest(s.value?.data ?? s.value);
    })();
  }, []);

  // Period comparison: the explicit date window (or the last 30 days) versus
  // the same-length window immediately before it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const dayMs = 864e5;
      const end = to ? new Date(`${to}T23:59:59`) : new Date();
      const start = from ? new Date(from) : new Date(end.getTime() - 30 * dayMs);
      if (!(start < end)) { setCompare(null); return; }
      const span = end.getTime() - start.getTime();
      const prevEnd = new Date(start.getTime() - 1000);
      const prevStart = new Date(prevEnd.getTime() - span);
      const base = new URLSearchParams(dimsQuery);
      base.delete('from_time'); base.delete('to_time');
      const win = (a, b) => {
        const p = new URLSearchParams(base);
        p.set('from_time', a.toISOString());
        p.set('to_time', b.toISOString());
        return p.toString();
      };
      const [cur, prev] = await Promise.allSettled([
        call('getReportsSummary', win(start, end)),
        call('getReportsSummary', win(prevStart, prevEnd)),
      ]);
      if (cancelled) return;
      const unwrap = (r) => (r.status === 'fulfilled' && r.value?.status !== false) ? (r.value?.data ?? r.value)?.filtered : null;
      const currentF = unwrap(cur);
      const prevF = unwrap(prev);
      if (currentF && prevF) {
        setCompare({ current: currentF, previous: prevF, days: Math.round(span / dayMs) });
      } else {
        setCompare(null);
      }
    })();
    return () => { cancelled = true; };
  }, [dimsQuery, from, to, lastSync]);

  // Per-incident comms for the report tab.
  useEffect(() => {
    setReportComms(null);
    if (!reportId) return;
    let cancelled = false;
    (async () => {
      const r = await call('getIncidentComms', reportId).catch(() => null);
      if (cancelled) return;
      const d = r?.status === false ? null : (r?.data ?? r);
      if (d && d.messages != null) setReportComms(d);
    })();
    return () => { cancelled = true; };
  }, [reportId, reportTimeline]);

  // Report operations helpers.
  const downloadPdf = async () => {
    if (!reportId) return;
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${ApiEndPoint}/api/reports/incidents/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || !(res.headers.get('content-type') || '').includes('pdf')) throw new Error('pdf failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${reportBase && isDrill(reportBase) ? 'After-Action-Review' : 'Post-Incident-Review'}-${reportId}.pdf`;
      a.click();
    } catch {
      window.print();
    }
  };
  const toggleDigest = async () => {
    const next = !(digest?.enabled);
    const r = await call('saveReportsSchedule', { enabled: next, frequency: digest?.frequency || 'weekly' }).catch(() => null);
    if (r?.status !== false && r) setDigest(r?.data ?? r);
  };
  const saveBenchmarks = async () => {
    const r = await call('saveReportsBenchmarks', benchDraft).catch(() => null);
    if (r?.status !== false && r) { setBenchmarks(r?.data ?? r); setBenchOpen(false); }
  };
  const scoreObjective = async (score) => {
    const r = await call('scoreIncidentObjective', reportId, { score }).catch(() => null);
    if (r?.status !== false) {
      setReportDetail(prev => prev ? { ...prev, objective_score: score } : prev);
      fetchAll();
    }
  };
  const persistViews = (views) => {
    setSavedViews(views);
    try { localStorage.setItem('reportsSavedViews', JSON.stringify(views)); } catch { /* ignore */ }
  };
  const applyView = (view) => {
    const s = view.state || {};
    setScope(s.scope || 'all'); setFrom(s.from || ''); setTo(s.to || '');
    setTypeF(s.typeF || 'all'); setEventF(s.eventF || 'all'); setTriggerF(s.triggerF || 'all');
    setLocationF(s.locationF || 'all'); setSeverityF(s.severityF || 'all');
    setStatusF(s.statusF || 'all'); setSearch(s.search || '');
  };

  // Early warnings live in ForeSight — the notification log is for alerts
  // delivered to responders, so the feed noise is filtered out here. The
  // date-range and search filters apply; classification filters don't map to
  // account-level notifications.
  const visibleNotifications = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromD = from ? new Date(from) : null;
    const toD = to ? new Date(`${to}T23:59:59`) : null;
    return notifications.filter(n => {
      if (String(n.type || '').toLowerCase() === 'early_warning') return false;
      const d = n.created_at ? new Date(n.created_at) : null;
      if (fromD && d && d < fromD) return false;
      if (toD && d && d > toD) return false;
      if (q && !`${n.type || ''} ${n.title || ''} ${n.subtitle || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [notifications, from, to, search]);

  // Scenario-container records never appear in reporting; the list endpoint
  // can return the same incident twice, so dedupe by id to keep counts honest.
  const records = useMemo(() => {
    const seen = new Set();
    return incidents.filter(i => {
      if (i.is_scenario || !i.id || seen.has(i.id)) return false;
      seen.add(i.id);
      return true;
    });
  }, [incidents]);
  const scopeCounts = useMemo(() => ({
    all: records.length,
    real: records.filter(i => !isDrill(i)).length,
    drill: records.filter(isDrill).length,
    sos: records.filter(isSos).length,
  }), [records]);
  const scoped = useMemo(() => {
    if (scope === 'real') return records.filter(i => !isDrill(i));
    if (scope === 'drill') return records.filter(isDrill);
    if (scope === 'sos') return records.filter(isSos);
    return records;
  }, [records, scope]);

  // Smart filter options: derived from the records in the active scope so
  // every dropdown only offers values that actually exist there, with counts.
  // Type › Event › Trigger cascade: each level narrows the next.
  const countedOptions = useCallback((rows, pick) => {
    const counts = new Map();
    rows.forEach(e => {
      const value = pick(e);
      (Array.isArray(value) ? value : [value]).forEach(v => {
        if (v) counts.set(v, (counts.get(v) || 0) + 1);
      });
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }));
  }, []);
  const typeOptions = useMemo(() => countedOptions(scoped, e => e.incident_type), [scoped, countedOptions]);
  const typeNarrowed = useMemo(() => typeF === 'all' ? scoped : scoped.filter(e => e.incident_type === typeF), [scoped, typeF]);
  const eventOptions = useMemo(() => countedOptions(typeNarrowed, e => e.event), [typeNarrowed, countedOptions]);
  const eventNarrowed = useMemo(() => eventF === 'all' ? typeNarrowed : typeNarrowed.filter(e => e.event === eventF), [typeNarrowed, eventF]);
  const triggerOptions = useMemo(() => countedOptions(eventNarrowed, e => e.trigger), [eventNarrowed, countedOptions]);
  const locationOptions = useMemo(() => countedOptions(scoped, e => e.location_names), [scoped, countedOptions]);
  const severityOptions = useMemo(() => {
    const present = new Map(countedOptions(scoped, e => e.severity).map(o => [o.value, o.count]));
    return ['High', 'Medium', 'Low'].map(s => ({ value: s, count: present.get(s) || 0 }));
  }, [scoped, countedOptions]);
  const statusOptions = useMemo(() => countedOptions(scoped, e => e.status), [scoped, countedOptions]);
  const types = useMemo(() => typeOptions.map(o => o.value), [typeOptions]);

  const onTypeChange = (v) => { setTypeF(v); setEventF('all'); setTriggerF('all'); };
  const onEventChange = (v) => { setEventF(v); setTriggerF('all'); };
  const clearFilters = () => {
    setFrom(''); setTo(''); setTypeF('all'); setEventF('all'); setTriggerF('all');
    setLocationF('all'); setSeverityF('all'); setStatusF('all'); setSearch('');
  };
  const activeFilterCount = [
    from, to,
    typeF !== 'all' && typeF, eventF !== 'all' && eventF, triggerF !== 'all' && triggerF,
    locationF !== 'all' && locationF, severityF !== 'all' && severityF,
    statusF !== 'all' && statusF, search.trim(),
  ].filter(Boolean).length;
  const filterChips = [
    from && { key: 'from', label: `From ${fmtDay(from)}`, clear: () => setFrom('') },
    to && { key: 'to', label: `To ${fmtDay(to)}`, clear: () => setTo('') },
    typeF !== 'all' && { key: 'type', label: `Type · ${typeF}`, clear: () => onTypeChange('all') },
    eventF !== 'all' && { key: 'event', label: `Event · ${eventF}`, clear: () => onEventChange('all') },
    triggerF !== 'all' && { key: 'trigger', label: `Trigger · ${triggerF}`, clear: () => setTriggerF('all') },
    locationF !== 'all' && { key: 'location', label: `Location · ${locationF}`, clear: () => setLocationF('all') },
    severityF !== 'all' && { key: 'severity', label: `Severity · ${severityF}`, clear: () => setSeverityF('all') },
    statusF !== 'all' && { key: 'status', label: statusF === 'open' ? 'Any active' : statusF === 'closed' ? 'Resolved / completed' : `Status · ${statusF.slice(2).replace(/_/g, ' ')}`, clear: () => setStatusF('all') },
    search.trim() && { key: 'search', label: `“${search.trim()}”`, clear: () => setSearch('') },
  ].filter(Boolean);
  const reportsCopilotPageContext = useMemo(() => ({
    scope,
    tab,
    filters: {
      ...(from ? { from_time: from } : {}),
      ...(to ? { to_time: `${to}T23:59:59` } : {}),
      ...(typeF !== 'all' ? { incident_type: typeF } : {}),
      ...(eventF !== 'all' ? { event: eventF } : {}),
      ...(triggerF !== 'all' ? { trigger: triggerF } : {}),
      ...(locationF !== 'all' ? { location: locationF } : {}),
      ...(severityF !== 'all' ? { severity: severityF } : {}),
      ...(statusF.startsWith('s:') ? { status: statusF.slice(2) } : statusF !== 'all' ? { status_mode: statusF } : {}),
      ...(search.trim() ? { q: search.trim() } : {}),
    },
  }), [scope, tab, from, to, typeF, eventF, triggerF, locationF, severityF, statusF, search]);

  // Two-stage filtering: dimension filters first (shared with the corrective
  // actions join), then free-text search on top.
  const dimFiltered = useMemo(() => {
    const fromD = from ? new Date(from) : null;
    const toD = to ? new Date(to + 'T23:59:59') : null;
    return scoped.filter(e => {
      if (typeF !== 'all' && e.incident_type !== typeF) return false;
      if (eventF !== 'all' && e.event !== eventF) return false;
      if (triggerF !== 'all' && e.trigger !== triggerF) return false;
      if (locationF !== 'all' && !(e.location_names || []).includes(locationF)) return false;
      if (severityF !== 'all' && e.severity !== severityF) return false;
      if (statusF === 'open' && !isOpen(e)) return false;
      if (statusF === 'closed' && isOpen(e)) return false;
      if (statusF.startsWith('s:') && e.status !== statusF.slice(2)) return false;
      const d = e.detected_at || e.created_at ? new Date(e.detected_at || e.created_at) : null;
      if (fromD && d && d < fromD) return false;
      if (toD && d && d > toD) return false;
      return true;
    });
  }, [scoped, typeF, eventF, triggerF, locationF, severityF, statusF, from, to]);
  const incidentSearchText = (e) => `${e.id} ${e.title} ${e.incident_type} ${e.event || ''} ${e.trigger || ''} ${(e.location_names || []).join(' ')} ${e.matched_scenario_name || ''}`.toLowerCase();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dimFiltered;
    return dimFiltered.filter(e => incidentSearchText(e).includes(q));
  }, [dimFiltered, search]);
  const dimFilteredIds = useMemo(() => new Set(dimFiltered.map(r => r.id)), [dimFiltered]);
  // Are any incident-dimension filters (everything except search) active?
  const dimensionalActive = Boolean(from || to || typeF !== 'all' || eventF !== 'all' || triggerF !== 'all' || locationF !== 'all' || severityF !== 'all' || statusF !== 'all');

  const caByIncident = useMemo(() => {
    const m = {};
    actions.forEach(a => { m[a.incident_id] = (m[a.incident_id] || 0) + 1; });
    return m;
  }, [actions]);

  // Overview metrics
  const medAck = useMemo(() => median(filtered.map(ackMinutes)), [filtered]);
  const medResolve = useMemo(() => median(filtered.map(resolveMinutes)), [filtered]);
  const trend = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let d = 29; d >= 0; d--) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - d);
      days.push({ key: day.toDateString(), label: day.toLocaleDateString([], { month: 'short', day: 'numeric' }), real: 0, drill: 0 });
    }
    const idx = Object.fromEntries(days.map((d, i) => [d.key, i]));
    filtered.forEach(e => {
      const t = e.detected_at || e.created_at;
      if (!t) return;
      const i = idx[new Date(t).toDateString()];
      if (i == null) return;
      days[i][isDrill(e) ? 'drill' : 'real'] += 1;
    });
    return days;
  }, [filtered]);
  const perfBySeverity = useMemo(() => ['High', 'Medium', 'Low'].map(sev => {
    const rows = filtered.filter(e => e.severity === sev);
    return {
      severity: sev,
      count: rows.length,
      open: rows.filter(isOpen).length,
      medAck: median(rows.map(ackMinutes)),
      medResolve: median(rows.map(resolveMinutes)),
    };
  }), [filtered]);

  const rowFor = (e) => ({
    ID: e.id,
    Title: e.title || '—',
    Kind: isDrill(e) ? (isSos(e) ? 'Drill (SOS)' : 'Drill') : (isSos(e) ? 'Real (SOS)' : 'Real'),
    Type: e.incident_type || '—',
    Severity: e.severity || '—',
    Status: isOpen(e) ? `Open (${e.status})` : `Closed (${e.status})`,
    Detected: fmt(e.detected_at || e.created_at),
    'Time to Ack': fmtDur(ackMinutes(e)),
    'Time to Resolve': fmtDur(resolveMinutes(e)),
    Schedule: e.planned_windows?.[0] ? `${fmtDay(e.planned_windows[0].start)} → ${fmtDay(e.planned_windows[0].end)}` : '—',
    CAs: caByIncident[e.id] || 0,
  });
  const toggleCol = (c) => setCols(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);
  const visibleCols = customMode ? ALL_COLS.filter(c => cols.includes(c)) : DEFAULT_COLS;

  // ── response report data ──────────────────────────────────────────────────
  const reportBase = useMemo(() => records.find(e => e.id === reportId), [records, reportId]);
  const rec = useMemo(() => reportBase ? { ...reportBase, ...(reportDetail || {}) } : null, [reportBase, reportDetail]);
  const recActions = useMemo(() => actions.filter(a => a.incident_id === reportId), [actions, reportId]);

  // Related-incident intelligence for the selected report record.
  const relatedIncidents = useMemo(() => {
    if (!rec) return null;
    const byId = new Map(records.map(r => [r.id, r]));
    const parent = rec.parent_incident_id ? (byId.get(rec.parent_incident_id) || { id: rec.parent_incident_id }) : null;
    const children = records.filter(r => r.parent_incident_id === rec.id);
    const plannedLink = rec.linked_planned_incident_id ? { id: rec.linked_planned_incident_id, planned: true } : null;
    const realLinks = (rec.linked_real_incidents || []).map(id => byId.get(id) || { id });
    return (parent || children.length || plannedLink || realLinks.length)
      ? { parent, children, plannedLink, realLinks }
      : null;
  }, [rec, records]);

  useEffect(() => {
    setReportDetail(null); setReportTimeline([]); setReportTimelineError(false); setReadinessChecks(null);
    if (!reportId) return;
    let cancelled = false;
    const load = async () => {
      const [detail, timeline, readiness] = await Promise.allSettled([
        call('getIncidentDetail', reportId),
        call('getincidentTimeline', reportId),
        call('getIncidentReadinessHistory', reportId, { limit: 10 }),
      ]);
      if (cancelled) return;
      if (detail.status === 'fulfilled' && detail.value?.status !== false) {
        const d = detail.value?.data?.incident || detail.value?.data || detail.value;
        if (d && d.id === reportId) setReportDetail(d);
      }
      if (timeline.status === 'fulfilled' && timeline.value?.status !== false) {
        const d = timeline.value?.data ?? timeline.value;
        setReportTimeline(Array.isArray(d) ? d : (d?.items || d?.timeline || []));
        setReportTimelineError(false);
      } else {
        setReportTimelineError(true);
      }
      if (readiness.status === 'fulfilled' && readiness.value?.status !== false) {
        const items = readiness.value?.data?.items;
        if (Array.isArray(items)) setReadinessChecks(items);
      }
    };
    load();
    // Live report: re-pull the timeline, detail and readiness every 30s.
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [reportId]);

  const acks = useMemo(() => reportTimeline.filter(t => t.group === 'acknowledgment'), [reportTimeline]);
  const ackRows = useMemo(() => acks.map(buildAckRow).sort((x, y) => new Date(x.notifiedAt || 0) - new Date(y.notifiedAt || 0)), [acks]);
  const ackDone = ackRows.filter(r => r.ackAt).length;
  const slaTracked = ackRows.filter(r => r.met === true || r.met === false);
  const slaMet = slaTracked.filter(r => r.met === true).length;
  const opsCounts = useMemo(() => {
    const m = {};
    reportTimeline.forEach(t => { if (t.group) m[t.group] = (m[t.group] || 0) + 1; });
    return m;
  }, [reportTimeline]);
  const injects = opsCounts.response_action || 0;

  // Fall back to the first timeline acknowledgment when the incident record
  // itself was never stamped with acknowledged_at.
  const firstAckAt = ackRows.map(r => r.ackAt).filter(Boolean).sort()[0] || null;
  const recAck = rec ? (ackMinutes(rec) ?? minutesBetween(rec.detected_at || rec.created_at, firstAckAt)) : null;
  const recResolve = rec ? resolveMinutes(rec) : null;
  const recDuration = rec ? (rec.duration_minutes ?? minutesBetween(rec.detected_at || rec.created_at, rec.resolved_at || null)) : null;
  const overdueRecActions = recActions.filter(a => a.status !== 'completed' && a.due_date && new Date(a.due_date) < new Date());

  const strengths = useMemo(() => {
    if (!rec) return [];
    const out = [];
    if (recAck != null && recAck <= 15) out.push(`Rapid acknowledgment — first response in ${fmtDur(recAck)}`);
    if (slaMet > 0) out.push(`${slaMet} of ${slaTracked.length} acknowledgment SLA(s) met on time`);
    if (ackDone === ackRows.length && ackRows.length > 0) out.push(`Full acknowledgment coverage — all ${ackRows.length} recipients responded`);
    if (injects > 0) out.push(`${injects} response action(s) executed across the timeline`);
    const doneCa = recActions.filter(a => a.status === 'completed').length;
    if (doneCa > 0) out.push(`${doneCa} corrective action(s) already completed`);
    if (!isDrill(rec) && rec.matched_scenario_id && rec.plan_state === 'active') out.push(`Pre-planned scenario "${rec.matched_scenario_name}" was activated for this incident`);
    return out;
  }, [rec, recAck, slaMet, slaTracked.length, ackDone, ackRows.length, injects, recActions]);

  const gaps = useMemo(() => {
    if (!rec) return [];
    const out = [];
    const missed = slaTracked.length - slaMet;
    if (missed > 0) out.push(`${missed} acknowledgment SLA(s) missed`);
    const pendingAcks = ackRows.length - ackDone;
    if (pendingAcks > 0) out.push(`${pendingAcks} recipient(s) never acknowledged`);
    const escalated = ackRows.filter(r => r.escalated).length;
    if (escalated > 0) out.push(`${escalated} acknowledgment(s) required escalation`);
    if (overdueRecActions.length > 0) out.push(`${overdueRecActions.length} corrective action(s) overdue`);
    if (recAck == null && isOpen(rec)) out.push('Incident has not been acknowledged yet');
    if (!isDrill(rec) && rec.matched_scenario_id && rec.plan_state !== 'active') out.push(`Matched scenario "${rec.matched_scenario_name}" was never activated`);
    return out;
  }, [rec, slaTracked.length, slaMet, ackRows, ackDone, overdueRecActions.length, recAck]);

  const extensiveMaterial = useMemo(() => {
    if (!rec) return '';
    return [
      `# ${rec.title || ''}`,
      `**${isDrill(rec) ? 'Exercise' : 'Real incident'} ${rec.id}** · ${rec.incident_type || '—'} · Severity: ${rec.severity || '—'} · Status: ${rec.status || '—'}${isSos(rec) ? ' · Origin: SOS' : ''}`,
      `**Locations:** ${(rec.location_names || []).join(', ') || '—'}`,
      '',
      '## Response Metrics',
      `- Time to acknowledge: ${fmtDur(recAck)}`,
      `- Time to resolve: ${fmtDur(recResolve)}`,
      `- Total duration: ${fmtDur(recDuration)}`,
      `- Acknowledgments: ${ackDone}/${ackRows.length} received`,
      `- SLA compliance: ${slaTracked.length ? `${pct(slaMet, slaTracked.length)}% (${slaMet}/${slaTracked.length})` : 'no SLA tracked'}`,
      '',
      '## Operations Log',
      ...OPS_GROUPS.filter(g => opsCounts[g.key]).map(g => `- ${g.label}: ${opsCounts[g.key]}`),
      '',
      ...(isDrill(rec) ? [
        '## Exercise Profile',
        `- Classification: ${rec.exercise_classification || '—'}`,
        `- Objective: ${rec.simulation_objective?.title || '—'}`,
        `- Participants: ${(rec.participating_entities || []).map(p => `${p.entity_name} (${p.role})`).join(', ') || '—'}`,
      ] : [
        '## Scenario Plan Performance',
        `- Matched scenario: ${rec.matched_scenario_name || 'none'}`,
        `- Plan state: ${rec.plan_state || '—'}`,
        `- Activation mode: ${rec.plan_activation_mode || '—'} (auto status: ${rec.plan_auto_activation_status || '—'})`,
        `- Activated roles: ${(rec.activated_role_assignments || []).length}`,
      ]),
      '',
      '## Acknowledgment Audit',
      ...(ackRows.length ? ackRows.map(r => `- ${r.name} (${r.unit}) — ${r.ackAt ? `acknowledged in ${fmtDur(r.actual)}` : 'no response'}${r.met === false ? ' · SLA MISSED' : r.met === true ? ' · SLA met' : ''}${r.escalated ? ' · escalated' : ''}`) : ['- No acknowledgment records']),
      '',
      '## Corrective Actions',
      ...(recActions.length ? recActions.map(a => `- ${a.title} — owner: ${a.owner_name || '—'}, due: ${fmtDay(a.due_date)}, status: ${a.status}`) : ['- None recorded']),
      '',
      '## Identified Strengths',
      ...(strengths.length ? strengths.map(s => `- ${s}`) : ['- None captured']),
      '',
      '## Identified Gaps',
      ...(gaps.length ? gaps.map(g => `- ${g}`) : ['- None identified']),
    ].join('\n');
  }, [rec, recAck, recResolve, recDuration, ackDone, ackRows, slaMet, slaTracked.length, opsCounts, recActions, strengths, gaps]);

  const reportOptions = useMemo(() => ({
    real: filtered.filter(e => !isDrill(e)),
    drill: filtered.filter(isDrill),
  }), [filtered]);
  const recordOptions = useMemo(() => [
    ...reportOptions.real.map(e => ({ value: e.id, label: `${e.id} · ${e.title || e.incident_type || 'Untitled'}`, badge: 'real', section: 'Real incidents' })),
    ...reportOptions.drill.map(e => ({ value: e.id, label: `${e.id} · ${e.title || e.incident_type || 'Untitled'}`, badge: 'drill', section: 'Exercises & drills' })),
  ], [reportOptions]);

  // Corrective-actions register rows: every incident-dimension filter gates
  // through the linked incident; search matches the action text or its
  // incident. Actions whose incident is unknown only appear on the unfiltered
  // global view. The tab's own status/overdue toggles apply last.
  const caRowsAll = useMemo(() => {
    const byId = new Map(records.map(r => [r.id, r]));
    const q = search.trim().toLowerCase();
    return actions.filter(a => {
      const inc = byId.get(a.incident_id);
      if (inc) {
        if (!dimFilteredIds.has(a.incident_id)) return false;
      } else if (scope !== 'all' || dimensionalActive) {
        return false;
      }
      if (q) {
        const hay = `${a.title || ''} ${a.owner_name || ''} ${a.incident_label || ''} ${a.incident_id || ''} ${inc ? incidentSearchText(inc) : ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).map(a => ({ ...a, incident: byId.get(a.incident_id) || null }));
  }, [actions, records, scope, dimFilteredIds, dimensionalActive, search]);
  const caRows = useMemo(() => caRowsAll.filter(a => {
    if (caStatusF !== 'all' && a.status !== caStatusF) return false;
    if (caOverdueOnly && !caIsOverdue(a)) return false;
    return true;
  }), [caRowsAll, caStatusF, caOverdueOnly]);
  const caStats = useMemo(() => {
    const s = { total: caRowsAll.length, open: 0, in_progress: 0, completed: 0, overdue: 0 };
    caRowsAll.forEach(a => {
      const st = (a.status || 'open').toLowerCase();
      if (s[st] != null) s[st] += 1;
      if (caIsOverdue(a)) s.overdue += 1;
    });
    return s;
  }, [caRowsAll]);

  const onCaStatusChange = async (a, status) => {
    const res = await call('updateCorrectiveAction', a.id, { status }).catch(() => null);
    if (res?.status !== false) fetchAll();
  };

  // Overview display values: server summary first, loaded-page fallback.
  // Corrective-action stats come from the fully filtered client rows so they
  // always agree with the register below them.
  const sf = summary?.filtered || null;
  const pillCounts = summary?.scope_counts || scopeCounts;
  const caUnavailable = sourceErrors.includes('actions');
  // Register rows come from the paginated server endpoint; the client page is
  // only the fallback for restricted sessions.
  const registerRows = registerData.error ? filtered : registerData.items;
  const registerTotal = registerData.error ? filtered.length : registerData.total;

  // Recurrence hotspots: the same classification striking the same place.
  const recurrenceHotspots = useMemo(() => {
    const m = new Map();
    filtered.forEach(e => {
      const key = `${e.incident_type || '—'}|${(e.location_names || [])[0] || 'Unknown location'}`;
      m.set(key, (m.get(key) || 0) + 1);
    });
    return [...m.entries()].filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1]).slice(0, 6)
      .map(([key, n]) => { const [type, loc] = key.split('|'); return { type, loc, n }; });
  }, [filtered]);
  const falseAlarms = sf ? (sf.by_status?.false_alarm || 0) : filtered.filter(e => e.status === 'false_alarm').length;

  // Data-quality hygiene over the loaded records.
  const dataQuality = useMemo(() => {
    const now = Date.now();
    const dupKeys = new Map();
    let dupSuspects = 0;
    records.forEach(e => {
      const day = e.detected_at || e.created_at ? new Date(e.detected_at || e.created_at).toDateString() : '';
      const key = `${e.incident_type || ''}|${(e.location_names || [])[0] || ''}|${day}`;
      const c = (dupKeys.get(key) || 0) + 1;
      dupKeys.set(key, c);
      if (c > 1) dupSuspects += 1;
    });
    return [
      { label: 'Unclassified (no type)', n: records.filter(e => !e.incident_type).length },
      { label: 'Missing location', n: records.filter(e => !(e.location_names || []).length).length },
      { label: 'Missing severity', n: records.filter(e => !e.severity).length },
      { label: 'Open > 14 days', n: records.filter(e => isOpen(e) && (e.detected_at || e.created_at) && (now - new Date(e.detected_at || e.created_at).getTime()) > 14 * 864e5).length },
      { label: 'Duplicate suspects (same type + place + day)', n: dupSuspects },
    ];
  }, [records]);
  const ov = sf ? {
    records: sf.records, open: sf.open, real: sf.real, drill: sf.drill, sos: sf.sos,
    medAck: sf.median_ack_minutes, medResolve: sf.median_resolve_minutes,
  } : {
    records: filtered.length,
    open: filtered.filter(isOpen).length,
    real: filtered.filter(e => !isDrill(e)).length,
    drill: filtered.filter(isDrill).length,
    sos: filtered.filter(isSos).length,
    medAck, medResolve,
  };
  const trendData = sf
    ? sf.trend.map(t => ({ label: new Date(`${t.date}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' }), real: t.real, drill: t.drill }))
    : trend;
  const sevChart = ['High', 'Medium', 'Low'].map(s => ({
    label: s,
    value: sf ? (sf.by_severity?.[s] || 0) : filtered.filter(e => e.severity === s).length,
    color: SEVERITY_STYLE[s].dot,
  }));
  const statusChart = sf
    ? Object.entries(sf.by_status || {}).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }))
    : [...new Set(filtered.map(e => e.status).filter(Boolean))].map(s => ({ label: s, value: filtered.filter(e => e.status === s).length }));
  const typeChart = sf
    ? (sf.by_type || []).slice(0, 8)
    : types.map(t => ({ label: t, value: filtered.filter(e => e.incident_type === t).length })).sort((a, b) => b.value - a.value).slice(0, 8);
  const perfTable = sf
    ? (sf.performance_by_severity || []).map(r => ({ severity: r.severity, count: r.count, open: r.open, medAck: r.median_ack_minutes, medResolve: r.median_resolve_minutes }))
    : perfBySeverity;
  const currentReportView = TABS.find((item) => item.id === tab) || TABS[0];
  const currentReportViewGroup = REPORT_VIEW_GROUPS.find((group) => group.ids.includes(currentReportView.id))?.label || REPORT_VIEW_GROUPS[0].label;
  const CurrentReportViewIcon = currentReportView.icon;

  if (showReportsCopilot) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 bg-[#F8FAFB]" data-testid="reports-center">
        <ReportsCopilotModal
          embedded
          onClose={() => setShowReportsCopilot(false)}
          pageContext={reportsCopilotPageContext}
        />
      </div>
    );
  }

  return (
    <div className="h-full w-full flex-1 overflow-y-auto bg-[#f8fafc] px-4 py-5 sm:px-6 lg:px-8 xl:px-10" data-testid="reports-center">
      <div className="mx-auto w-full max-w-[1920px]" data-testid="reports-content-shell">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-testid="reports-header">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-lg shadow-indigo-200"><FileBarChart className="h-6 w-6 text-white" /></div>
          <div>
            <h1 className="text-[26px] font-black tracking-[-0.035em] text-slate-950 sm:text-[28px]">Reports Center</h1>
            <p className="text-[13px] font-medium leading-snug text-slate-500 sm:text-sm">Global reporting across real incidents, exercises &amp; drills</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap lg:justify-end" data-testid="reports-header-actions">
          <button onClick={() => setShowGuide(true)} data-testid="reports-guide-open" title="How the Reports Center works — every tab and number explained"
            className="flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 text-[13px] font-bold text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-indigo-600">
            <BookOpen className="h-4 w-4" />Guide
          </button>
          {canManageCorrectiveActions && digest && (
            <button onClick={toggleDigest} data-testid="reports-digest-toggle" title="Send a periodic response digest to administrators as a notification"
              className={`flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-[13px] font-bold shadow-sm transition-colors ${digest.enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>
              <BellRing className="h-4 w-4" />{digest.enabled ? `${digest.frequency === 'monthly' ? 'Monthly' : 'Weekly'} digest on` : 'Digest off'}
            </button>
          )}
          <div className="flex min-h-10 min-w-[138px] items-center justify-center px-1" data-testid="reports-live-status">
            {lastSync && <span className="flex items-center justify-center gap-1.5 text-[13px] font-black text-emerald-600" data-testid="reports-live-badge"><span className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(34,197,94,0.12)] animate-pulse" />Live · {lastSync.toLocaleTimeString()}</span>}
          </div>
        </div>
      </div>

      {/* Report workspace actions */}
      <div className="mb-5 flex w-full flex-col gap-2.5 sm:flex-row sm:items-stretch" data-testid="reports-workspace-actions">
        <div className="relative min-w-0 flex-1" ref={viewMenuRef} data-testid="reports-view-selector">
          <button
            ref={viewMenuToggleRef}
            type="button"
            onClick={() => setViewMenuOpen((open) => !open)}
            className="group flex min-h-[60px] w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-left shadow-[0_12px_30px_-24px_rgba(15,23,42,0.65)] transition duration-200 hover:border-slate-300 hover:shadow-[0_16px_34px_-24px_rgba(15,23,42,0.72)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            aria-haspopup="menu"
            aria-expanded={viewMenuOpen}
            aria-controls="reports-view-menu"
            data-testid="reports-view-selector-toggle"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <CurrentReportViewIcon className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-[9px] font-black uppercase tracking-[0.16em] text-slate-400" data-testid="reports-view-overline">REPORT VIEW</span>
                <span className="h-px w-4 shrink-0 bg-slate-200" aria-hidden="true" />
                <span className="truncate text-[10px] font-black text-slate-500" data-testid="reports-current-view-group">{currentReportViewGroup}</span>
              </span>
              <span className="mt-0.5 block truncate text-lg font-black leading-tight tracking-[-0.02em] text-slate-950" data-testid="reports-current-view">{currentReportView.label}</span>
            </span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors group-hover:border-slate-300 group-hover:bg-slate-50">
              <ChevronDown className={`h-4 w-4 transition-transform ${viewMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </span>
          </button>

          {viewMenuOpen && (
            <div
              id="reports-view-menu"
              role="menu"
              aria-label="Choose a Reports Center view"
              className="absolute left-0 top-full z-40 mt-2 grid w-full min-w-0 gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl shadow-slate-900/15 sm:w-[min(760px,calc(100vw-3rem))] sm:grid-cols-3"
              data-testid="reports-view-menu"
            >
              {REPORT_VIEW_GROUPS.map((group) => (
                <section key={group.label} role="group" aria-label={group.label} className="min-w-0 rounded-xl bg-slate-50/80 p-2">
                  <p className="px-2 pb-1.5 pt-1 text-[9px] font-black uppercase tracking-[0.12em] text-slate-400">{group.label}</p>
                  <div className="space-y-0.5">
                    {group.ids.map((id) => {
                      const item = TABS.find((candidate) => candidate.id === id);
                      if (!item) return null;
                      const ItemIcon = item.icon;
                      const active = tab === item.id;
                      return (
                        <button
                          key={item.id}
                          ref={(node) => { reportViewRefs.current[item.id] = node; }}
                          type="button"
                          role="menuitemradio"
                          aria-checked={active}
                          onClick={() => {
                            setTab(item.id);
                            setViewMenuOpen(false);
                          }}
                          data-testid={`reports-tab-${item.id}`}
                          className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${active
                            ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200'
                            : 'text-slate-600 hover:bg-white hover:text-slate-950'}`}
                        >
                          <ItemIcon className={`h-4 w-4 shrink-0 ${active ? 'text-indigo-600' : 'text-slate-400'}`} aria-hidden="true" />
                          <span className="min-w-0 flex-1 leading-snug">{item.label}</span>
                          {active && <Check className="h-3.5 w-3.5 shrink-0 text-indigo-600" aria-hidden="true" />}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowReportsCopilot(true)}
          data-testid="reports-copilot-open"
          title="Ask questions across supported authorized incident and operational reporting data"
          className="inline-flex min-h-[60px] w-full shrink-0 items-center justify-center rounded-2xl border border-slate-950 bg-slate-950 px-5 text-sm font-black tracking-[-0.01em] text-white shadow-[0_16px_30px_-20px_rgba(15,23,42,0.85)] transition duration-200 hover:-translate-y-0.5 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2 sm:w-auto sm:min-w-[136px]"
        >
          Ask Reports
        </button>
      </div>

      {sourceErrors.length > 0 && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert" data-testid="reports-source-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p><span className="font-bold">Some report data is unavailable to this read-only session:</span> {sourceErrors.map((source) => REPORT_SOURCE_LABELS[source]).join(', ')}. Available institution-scoped records are shown below.</p>
        </div>
      )}

      {/* Smart filters */}
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white shadow-[0_14px_34px_-28px_rgba(15,23,42,0.55)]" data-testid="reports-filters">
        {/* Scope + filter controls */}
        <div className="flex flex-wrap items-center gap-3 px-4 pb-3 pt-4" data-testid="reports-filter-toolbar">
          <div className="inline-flex max-w-full flex-wrap gap-1 rounded-xl bg-slate-100 p-1" data-testid="reports-scope">
            {SCOPES.map(s => (
              <button key={s.id} onClick={() => setScope(s.id)} data-testid={`reports-scope-${s.id}`}
                className={`flex min-h-9 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-black transition-colors ${scope === s.id ? s.active : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>
                <s.icon className="h-3.5 w-3.5" />{s.label}
                <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-black ${scope === s.id ? 'bg-white/15 text-white' : 'bg-slate-200/80 text-slate-400'}`}>{pillCounts[s.id]}</span>
              </button>
            ))}
          </div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
            <div className="text-right" data-testid="reports-filter-count">
              <p className="text-lg font-black leading-none text-slate-950"><span className="text-indigo-600">{sf ? sf.records : filtered.length}</span><span className="ml-1 text-sm font-bold text-slate-400">/ {pillCounts[scope]}</span></p>
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} data-testid="reports-clear-filters" className="flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-rose-600 border border-gray-200 hover:border-rose-200 bg-white px-2.5 py-1.5 rounded-lg transition-colors">
                <XCircle className="w-3.5 h-3.5" />Clear all
              </button>
            )}
            <button onClick={() => setFiltersOpen(v => !v)} data-testid="reports-filters-toggle" aria-expanded={filtersOpen}
              className={`flex h-9 items-center gap-1.5 rounded-xl border px-3 text-xs font-black transition-colors ${filtersOpen ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>
              <Filter className="h-3.5 w-3.5" />Filters
              {activeFilterCount > 0 && <span className="text-[9px] font-black px-1.5 rounded-full bg-indigo-600 text-white">{activeFilterCount}</span>}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Saved views */}
        {savedViews.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap px-4 pb-2" data-testid="reports-saved-views">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-300 mr-1">Views</span>
            {savedViews.map(view => (
              <span key={view.name} className="group inline-flex items-center gap-1 text-[11px] font-bold text-gray-600 bg-gray-50 border border-gray-200 hover:border-indigo-300 hover:text-indigo-700 rounded-full pl-2.5 pr-1 py-0.5 transition-colors">
                <button onClick={() => applyView(view)} title="Apply this view">{view.name}</button>
                <button onClick={() => persistViews(savedViews.filter(v => v.name !== view.name))} title="Delete view" className="text-gray-300 hover:text-rose-500 p-0.5"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}

        {/* Responsive field grid */}
        {filtersOpen && (
          <div className="grid grid-cols-1 gap-2 px-4 pb-3 pt-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-8 [&>*]:!min-w-0 [&>*]:!px-2.5 [&>*]:!pb-1.5 [&>*]:!pt-1" data-testid="reports-detailed-filters">
            <FilterField icon={Calendar} label="From" active={!!from} chevron={false}><input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="report-from" className={`${FILTER_INPUT_CLS} pr-0`} /></FilterField>
            <FilterField icon={Calendar} label="To" active={!!to} chevron={false}><input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="report-to" className={`${FILTER_INPUT_CLS} pr-0`} /></FilterField>
            <FilterField icon={Tag} label="Incident type" active={typeF !== 'all'}>
              <SearchSelect value={typeF} onChange={onTypeChange} options={typeOptions} allLabel="All types" testId="report-type" />
            </FilterField>
            <FilterField icon={Layers} label="Event" active={eventF !== 'all'} muted={eventOptions.length === 0}>
              <SearchSelect value={eventF} onChange={onEventChange} options={eventOptions} allLabel={eventOptions.length ? 'All events' : 'No events'} disabled={eventOptions.length === 0} testId="report-event" />
            </FilterField>
            <FilterField icon={Zap} label="Trigger" active={triggerF !== 'all'} muted={triggerOptions.length === 0}>
              <SearchSelect value={triggerF} onChange={setTriggerF} options={triggerOptions} allLabel={triggerOptions.length ? 'All triggers' : 'No triggers'} disabled={triggerOptions.length === 0} testId="report-trigger" />
            </FilterField>
            <FilterField icon={MapPin} label="Location" active={locationF !== 'all'}>
              <SearchSelect value={locationF} onChange={setLocationF} options={locationOptions} allLabel="All locations" testId="report-location" />
            </FilterField>
            <FilterField icon={Gauge} label="Severity" active={severityF !== 'all'}>
              <select value={severityF} onChange={e => setSeverityF(e.target.value)} data-testid="report-severity" className={FILTER_INPUT_CLS}>
                <option value="all">All severities</option>
                {severityOptions.map(o => <option key={o.value} value={o.value}>{o.value} ({o.count})</option>)}
              </select>
            </FilterField>
            <FilterField icon={Activity} label="Status" active={statusF !== 'all'}>
              <select value={statusF} onChange={e => setStatusF(e.target.value)} data-testid="report-status" className={FILTER_INPUT_CLS}>
                <option value="all">All statuses</option>
                <option value="open">Any active</option>
                <option value="closed">Resolved / completed</option>
                <optgroup label={scope === 'drill' ? 'Drill statuses' : scope === 'real' ? 'Incident statuses' : 'Statuses in scope'}>
                  {statusOptions.map(o => <option key={o.value} value={`s:${o.value}`}>{String(o.value).replace(/_/g, ' ')} ({o.count})</option>)}
                </optgroup>
              </select>
            </FilterField>
          </div>
        )}

        {/* Search follows the view and detailed filters so the page reads from scope to query. */}
        <div className="px-4 pb-4 pt-1" data-testid="reports-search-row">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ID, title, type, event, trigger, location, scenario — also filters the audit log…"
              className="block h-11 w-full rounded-xl border border-slate-100 bg-slate-50/80 pl-12 pr-10 text-sm font-semibold text-slate-900 outline-none transition-all placeholder:font-medium placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
              data-testid="reports-search-input"
            />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500" aria-label="Clear search"><X className="w-4 h-4" /></button>}
          </div>
        </div>

        {/* Active filter chips */}
        {filterChips.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap px-4 py-2.5 border-t border-gray-100 bg-indigo-50/40 rounded-b-2xl" data-testid="reports-filter-chips">
            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mr-1">Active</span>
            {filterChips.map(chip => (
              <button key={chip.key} onClick={chip.clear} title="Remove filter"
                className="group flex items-center gap-1.5 text-[11px] font-bold text-indigo-700 bg-white border border-indigo-200 hover:border-rose-200 hover:text-rose-600 px-2.5 py-1 rounded-full transition-colors shadow-sm">
                {chip.label}
                <X className="w-3 h-3 text-indigo-300 group-hover:text-rose-400" />
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? <div className="p-12 text-center text-gray-400">Loading reports…</div> : (
        <>
          {/* ── OVERVIEW ─────────────────────────────────────────────────── */}
          {tab === 'overview' && (
            <div className="space-y-4" data-testid="report-overview">
              {caUnavailable && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">Corrective-action metrics are unavailable; they are shown as — instead of a misleading zero.</p>}
              {summaryError && <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800" data-testid="reports-summary-fallback">Server summary is unavailable — metrics below are computed from the {records.length} loaded records.</p>}
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
                <Kpi label="Records" value={ov.records} tone="text-gray-900" icon={Layers} />
                <Kpi label="Open now" value={ov.open} tone="text-emerald-600" icon={Activity} />
                <Kpi label="Real incidents" value={ov.real} tone="text-rose-600" icon={Siren} />
                <Kpi label="Drills" value={ov.drill} tone="text-indigo-600" icon={FlaskConical} />
                <Kpi label="SOS-origin" value={ov.sos} tone="text-red-600" icon={BellRing} />
                <Kpi label="Median ack" value={fmtDur(ov.medAck)}
                  tone={benchmarks && ov.medAck != null ? (ov.medAck <= benchmarks.mtta_minutes ? 'text-emerald-600' : 'text-rose-600') : 'text-sky-600'}
                  sub={benchmarks ? `target ≤ ${fmtDur(benchmarks.mtta_minutes)}` : undefined} icon={Clock} />
                <Kpi label="Median resolve" value={fmtDur(ov.medResolve)}
                  tone={benchmarks && ov.medResolve != null ? (ov.medResolve <= benchmarks.mttr_minutes ? 'text-emerald-600' : 'text-rose-600') : 'text-blue-600'}
                  sub={benchmarks ? `target ≤ ${fmtDur(benchmarks.mttr_minutes)}` : undefined} icon={CheckCircle2} />
                <Kpi label="Overdue actions" value={caUnavailable ? '—' : caStats.overdue} tone="text-rose-600" icon={AlertTriangle} />
              </div>

              {compare && (
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="reports-period-compare">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                    Period comparison — {from || to ? 'selected window' : `last ${compare.days} days`} vs the {compare.days} days before
                  </p>
                  <div className="flex flex-wrap gap-6">
                    <DeltaStat label="New records" cur={compare.current.records} prev={compare.previous.records} />
                    <DeltaStat label="High severity" cur={compare.current.by_severity?.High} prev={compare.previous.by_severity?.High} goodWhenDown />
                    <DeltaStat label="Median ack" cur={compare.current.median_ack_minutes} prev={compare.previous.median_ack_minutes} fmtFn={fmtDur} goodWhenDown />
                    <DeltaStat label="Median resolve" cur={compare.current.median_resolve_minutes} prev={compare.previous.median_resolve_minutes} fmtFn={fmtDur} goodWhenDown />
                    <DeltaStat label="Drills run" cur={compare.current.drill} prev={compare.previous.drill} />
                  </div>
                </div>
              )}

              <TrendChart title="30-day activity" data={trendData} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Donut title="By severity" data={sevChart} />
                <BarChart title="By status" data={statusChart} />
                <BarChart title="By type (top 8)" data={typeChart} />
                <Donut title={`Corrective actions — ${SCOPES.find(s => s.id === scope)?.label || scope}`} data={['open', 'in_progress', 'completed'].map((s, i) => ({ label: s, value: caUnavailable ? 0 : caStats[s], color: [PALETTE[1], PALETTE[4], PALETTE[2]][i] }))} />
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 p-4 pb-2">Response performance by severity</p>
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50"><th className="text-left px-4 py-2">Severity</th><th className="text-left px-4 py-2">Records</th><th className="text-left px-4 py-2">Open</th><th className="text-left px-4 py-2">Median time to ack</th><th className="text-left px-4 py-2">Median time to resolve</th></tr></thead>
                  <tbody>
                    {perfTable.map(r => (
                      <tr key={r.severity} className="border-t border-gray-50">
                        <td className="px-4 py-2.5"><SeverityChip severity={r.severity} /></td>
                        <td className="px-4 py-2.5 font-bold text-gray-800">{r.count}</td>
                        <td className="px-4 py-2.5 text-gray-600">{r.open}</td>
                        <td className="px-4 py-2.5 text-gray-600">{fmtDur(r.medAck)}</td>
                        <td className="px-4 py-2.5 text-gray-600">{fmtDur(r.medResolve)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Recurrence hotspots + false alarms */}
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="reports-recurrence">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Recurrence hotspots</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${falseAlarms ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>
                      {falseAlarms} false alarm{falseAlarms === 1 ? '' : 's'} ({pct(falseAlarms, ov.records)}%)
                    </span>
                  </div>
                  {recurrenceHotspots.length === 0 ? <p className="text-xs text-gray-400">No repeating type + location pairs in this view.</p> : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400"><th className="text-left py-1">Type</th><th className="text-left py-1">Location</th><th className="text-right py-1">Occurrences</th></tr></thead>
                      <tbody>
                        {recurrenceHotspots.map((h, i) => (
                          <tr key={i} className="border-t border-gray-50">
                            <td className="py-1.5 font-semibold text-gray-800">{h.type}</td>
                            <td className="py-1.5 text-gray-600">{h.loc}</td>
                            <td className="py-1.5 text-right"><span className={`font-black ${h.n >= 5 ? 'text-rose-600' : 'text-amber-600'}`}>{h.n}×</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Data quality */}
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="reports-data-quality">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Data quality — record hygiene</p>
                  <div className="space-y-1.5">
                    {dataQuality.map((d) => (
                      <div key={d.label} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{d.label}</span>
                        <span className={`font-black ${d.n === 0 ? 'text-emerald-600' : d.n > 10 ? 'text-rose-600' : 'text-amber-600'}`}>{d.n === 0 ? '✓ 0' : d.n}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">Fix these in the register — every other number in this center depends on clean records.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── REGISTER ─────────────────────────────────────────────────── */}
          {tab === 'register' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="report-register">
              <div className="flex items-center justify-between p-3 border-b border-gray-100 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-bold text-gray-800">{registerTotal} records{!registerData.error && registerRows.length < registerTotal ? ` · showing ${registerRows.length}` : ''}</p>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600"><input type="checkbox" checked={customMode} onChange={e => setCustomMode(e.target.checked)} data-testid="report-custom-toggle" />Custom report</label>
                </div>
                <button onClick={() => downloadCsv(`${scope}_register.csv`, registerRows.map(rowFor))} data-testid="report-export-csv" className="flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" />Export CSV</button>
              </div>
              {customMode && (
                <div className="flex flex-wrap gap-3 p-3 bg-indigo-50/40 border-b border-gray-100">
                  {ALL_COLS.map(c => <label key={c} className="flex items-center gap-1 text-xs text-gray-700"><input type="checkbox" checked={cols.includes(c)} onChange={() => toggleCol(c)} />{c}</label>)}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-400">{visibleCols.map(c => <th key={c} className="text-left px-4 py-2.5 whitespace-nowrap">{c}</th>)}</tr></thead>
                  <tbody>
                    {registerRows.length === 0 && !registerData.loading && <tr><td colSpan={visibleCols.length} className="p-8 text-center text-gray-400">No records match the filters.</td></tr>}
                    {registerRows.map(e => { const r = rowFor(e); return (
                      <tr key={e.id} className="border-t border-gray-50 hover:bg-gray-50" data-testid={`report-row-${e.id}`}>
                        {visibleCols.map(c => (
                          <td key={c} className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                            {c === 'ID' ? <Link to={`/incident-details/${e.id}`} className="text-indigo-600 hover:underline flex items-center gap-1">{e.id}<ExternalLink className="w-3 h-3" /></Link>
                              : c === 'Kind' ? <KindBadge rec={e} />
                              : c === 'Severity' ? <SeverityChip severity={e.severity} />
                              : c === 'Status' ? <StatusChip status={e.status} />
                              : c === 'Title' ? <span className="whitespace-normal">{r.Title}</span>
                              : r[c]}
                          </td>
                        ))}
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
              {!registerData.error && registerRows.length < registerTotal && (
                <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-xs text-gray-400">{registerRows.length} of {registerTotal} shown{registerData.loading ? ' · loading…' : ''}</p>
                  <button onClick={() => loadRegister(true)} disabled={registerData.loading} data-testid="register-load-more" className="text-xs font-bold text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-md disabled:opacity-50">
                    {registerData.loading ? 'Loading…' : 'Load 100 more'}
                  </button>
                </div>
              )}
              <div className="p-4 border-t border-gray-100">
                {sourceErrors.includes('scenarios') ? <p className="text-xs font-semibold text-amber-700">Scenario library summary is not available to this session.</p> : <>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Scenario library ({scenarios.length})</p>
                  <div className="flex gap-2 flex-wrap text-xs">
                    {['Active', 'Draft', 'Archived'].map(s => (
                      <span key={s} className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">{s}: {scenarios.filter(x => x.status === s).length}</span>
                    ))}
                  </div>
                </>}
              </div>
            </div>
          )}

          {/* ── RESPONSE REPORT (AAR / Post-Incident Review) ─────────────── */}
          {tab === 'report' && (
            <div className="space-y-4" data-testid="report-response">
              <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-end gap-3 flex-wrap print:hidden">
                <div className="flex-1 min-w-[300px] max-w-2xl flex">
                  <FilterField icon={FileText} label="Select record" active={!!reportId}>
                    <SearchSelect
                      value={reportId || 'all'}
                      onChange={(v) => setReportId(v === 'all' ? '' : v)}
                      options={recordOptions}
                      allLabel="— Choose an incident or drill —"
                      testId="aar-select"
                    />
                  </FilterField>
                </div>
                {rec && <button onClick={() => setShowExtensive(true)} data-testid="reports-extensive-analysis-btn" className="ml-auto flex items-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 px-3 py-1.5 rounded-md"><FileText className="w-3.5 h-3.5" />Extensive Analysis</button>}
                {rec && <button onClick={downloadPdf} data-testid="report-pdf-download" className="flex items-center gap-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-md"><Printer className="w-3.5 h-3.5" />Export report (PDF)</button>}
              </div>

              {!rec ? (
                <div className="p-12 text-center bg-white border border-gray-200 rounded-xl">
                  <FileText className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 font-semibold">Select a record to generate its report</p>
                  <p className="text-sm text-gray-400 mt-1">Real incidents produce a Post-Incident Review; drills produce an After-Action Review — same engine, full response data.</p>
                </div>
              ) : (
                <div id="response-report-print" className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
                  <style>{`@media print {
                    body * { visibility: hidden; }
                    #response-report-print, #response-report-print * { visibility: visible; }
                    #response-report-print { position: absolute; left: 0; top: 0; width: 100%; border: none; }
                  }`}</style>
                  {reportTimelineError && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 print:hidden">The timeline is not available to this session. Timeline-based metrics are shown as zero.</p>}

                  {/* Report header */}
                  <div className="border-b border-gray-100 pb-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">{isDrill(rec) ? 'After-Action Review' : 'Post-Incident Review'}</p>
                      <KindBadge rec={rec} size="sm" />
                    </div>
                    <h2 className="text-xl font-black text-gray-900 mt-1">{rec.title || rec.incident_type || 'Untitled'}</h2>
                    <p className="text-sm text-gray-500">{rec.id} · {[rec.incident_type, rec.event, rec.trigger].filter(Boolean).join(' › ') || '—'}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <SeverityChip severity={rec.severity} />
                      <StatusChip status={rec.status} />
                      {isDrill(rec) && rec.exercise_classification && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{rec.exercise_classification}</span>}
                      {(rec.location_names || []).length > 0 && <span className="text-[11px] text-gray-500 font-semibold">📍 {(rec.location_names || []).join(', ')}</span>}
                    </div>
                    {isDrill(rec) && rec.planned_windows?.[0] && <p className="text-xs text-gray-500 mt-1.5">Schedule: {fmt(rec.planned_windows[0].start)} → {fmt(rec.planned_windows[0].end)}</p>}
                  </div>

                  {/* Response phases */}
                  <Section icon={Clock} title="Response timeline">
                    <div className="flex items-center gap-0 flex-wrap">
                      {[
                        { label: 'Detected', at: rec.detected_at || rec.created_at, delta: null },
                        { label: 'Acknowledged', at: rec.acknowledged_at, delta: recAck },
                        { label: 'Resolved', at: rec.resolved_at, delta: recResolve },
                      ].map((p, i) => (
                        <React.Fragment key={p.label}>
                          {i > 0 && <div className={`flex-1 h-0.5 min-w-[24px] mx-1 ${p.at ? 'bg-indigo-400' : 'bg-gray-200'}`} />}
                          <div className="text-center px-1">
                            <div className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center ${p.at ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                              {p.at ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                            </div>
                            <p className="text-[11px] font-bold text-gray-700 mt-1">{p.label}</p>
                            <p className="text-[10px] text-gray-500">{fmt(p.at)}</p>
                            {p.delta != null && <p className="text-[10px] font-black text-indigo-600">+{fmtDur(p.delta)}</p>}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </Section>

                  {/* KPIs */}
                  <Section icon={Activity} title="Performance indicators">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <Kpi label="Time to ack" value={fmtDur(recAck)} tone="text-sky-600" />
                      <Kpi label="Time to resolve" value={fmtDur(recResolve)} tone="text-blue-600" />
                      <Kpi label="Duration" value={fmtDur(recDuration)} tone="text-gray-700" />
                      <Kpi label="Acks received" value={`${ackDone}/${ackRows.length}`} tone="text-indigo-600" />
                      <Kpi label="SLA compliance" value={slaTracked.length ? `${pct(slaMet, slaTracked.length)}%` : '—'} sub={slaTracked.length ? `${slaMet}/${slaTracked.length} met` : 'no SLA tracked'} tone="text-emerald-600" />
                      <Kpi label="Corrective actions" value={sourceErrors.includes('actions') ? '—' : recActions.length} tone="text-amber-600" />
                    </div>
                  </Section>

                  {/* Scenario plan performance (real) / Exercise profile (drill) */}
                  {isDrill(rec) ? (
                    <Section icon={FlaskConical} title="Exercise profile">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="rounded-lg border border-gray-100 p-3 space-y-1.5">
                          <p><span className="font-bold text-gray-500 text-xs uppercase">Classification:</span> <span className="text-gray-800">{rec.exercise_classification || '—'}</span></p>
                          <p><span className="font-bold text-gray-500 text-xs uppercase">Objective:</span> <span className="text-gray-800">{rec.simulation_objective?.title || '—'}</span></p>
                          {rec.simulation_objective?.success_criteria && <p className="text-xs text-gray-600">{rec.simulation_objective.success_criteria}</p>}
                          {rec.simulation_objective && (
                            <div className="flex items-center gap-1.5 pt-1" data-testid="objective-scoring">
                              <span className="text-[10px] font-bold uppercase text-gray-400">Outcome:</span>
                              {['achieved', 'partial', 'missed'].map(s => (
                                <button key={s} disabled={!canManageCorrectiveActions} onClick={() => scoreObjective(s)}
                                  className={`text-[10px] font-black px-2 py-0.5 rounded-full border capitalize transition-colors ${rec.objective_score === s
                                    ? (s === 'achieved' ? 'bg-emerald-600 text-white border-emerald-600' : s === 'partial' ? 'bg-amber-500 text-white border-amber-500' : 'bg-rose-600 text-white border-rose-600')
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 disabled:opacity-50'}`}>
                                  {s}
                                </button>
                              ))}
                              {!rec.objective_score && <span className="text-[10px] text-gray-400">not scored yet</span>}
                            </div>
                          )}
                        </div>
                        <div className="rounded-lg border border-gray-100 p-3">
                          <p className="font-bold text-gray-500 text-xs uppercase mb-1.5">Participating entities</p>
                          {(rec.participating_entities || []).length === 0 ? <p className="text-xs text-gray-400">None recorded</p> : (
                            <ul className="text-xs text-gray-700 space-y-1">
                              {(rec.participating_entities || []).map((p, i) => <li key={i} className="flex items-center gap-1.5"><Users className="w-3 h-3 text-gray-400" />{p.entity_name} <span className="text-gray-400">· {p.role}{p.type ? ` · ${p.type}` : ''}</span></li>)}
                            </ul>
                          )}
                        </div>
                      </div>
                    </Section>
                  ) : (
                    <Section icon={Target} title="Scenario plan performance">
                      {!rec.matched_scenario_id ? (
                        <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg p-3">No pre-planned scenario matched this incident's classification.</p>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="rounded-lg border border-gray-100 p-3"><p className="text-[10px] font-bold uppercase text-gray-400">Matched scenario</p><p className="font-bold text-gray-800">{rec.matched_scenario_name || rec.matched_scenario_id}</p></div>
                          <div className="rounded-lg border border-gray-100 p-3"><p className="text-[10px] font-bold uppercase text-gray-400">Plan state</p><p className={`font-bold ${rec.plan_state === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>{rec.plan_state || 'planned'}</p>{rec.plan_activated_at && <p className="text-[10px] text-gray-500">{fmt(rec.plan_activated_at)}</p>}</div>
                          <div className="rounded-lg border border-gray-100 p-3"><p className="text-[10px] font-bold uppercase text-gray-400">Activation</p><p className="font-bold text-gray-800 capitalize">{rec.plan_activation_mode || '—'}</p>{rec.plan_auto_activation_status && <p className="text-[10px] text-gray-500 capitalize">auto: {rec.plan_auto_activation_status}{rec.plan_auto_activation_window_minutes ? ` (${rec.plan_auto_activation_window_minutes}m window)` : ''}</p>}</div>
                          <div className="rounded-lg border border-gray-100 p-3"><p className="text-[10px] font-bold uppercase text-gray-400">Roles activated</p><p className="font-bold text-gray-800">{(rec.activated_role_assignments || []).length}</p></div>
                        </div>
                      )}
                    </Section>
                  )}

                  {/* Related incidents */}
                  {relatedIncidents && (
                    <Section icon={Layers} title="Related incidents">
                      <div className="space-y-1.5 text-sm">
                        {relatedIncidents.parent && (
                          <p className="flex items-center gap-2"><span className="text-[10px] font-bold uppercase text-gray-400 w-28 shrink-0">Parent</span>
                            <Link to={`/incident-details/${relatedIncidents.parent.id}`} className="text-indigo-600 hover:underline">{relatedIncidents.parent.id}</Link>
                            <span className="text-gray-500">{relatedIncidents.parent.title || ''}</span>
                            {relatedIncidents.parent.status && <StatusChip status={relatedIncidents.parent.status} />}
                          </p>
                        )}
                        {relatedIncidents.plannedLink && (
                          <p className="flex items-center gap-2"><span className="text-[10px] font-bold uppercase text-gray-400 w-28 shrink-0">During event</span>
                            <Link to={`/incident-details/${relatedIncidents.plannedLink.id}`} className="text-indigo-600 hover:underline">{relatedIncidents.plannedLink.id}</Link>
                            <span className="text-gray-400 text-xs">this incident erupted during a planned event</span>
                          </p>
                        )}
                        {relatedIncidents.children.map(c => (
                          <p key={c.id} className="flex items-center gap-2"><span className="text-[10px] font-bold uppercase text-gray-400 w-28 shrink-0">Child</span>
                            <Link to={`/incident-details/${c.id}`} className="text-indigo-600 hover:underline">{c.id}</Link>
                            <span className="text-gray-500">{c.title || ''}</span>
                            <KindBadge rec={c} />
                            <StatusChip status={c.status} />
                          </p>
                        ))}
                        {relatedIncidents.realLinks.map(c => (
                          <p key={c.id} className="flex items-center gap-2"><span className="text-[10px] font-bold uppercase text-gray-400 w-28 shrink-0">Real during</span>
                            <Link to={`/incident-details/${c.id}`} className="text-indigo-600 hover:underline">{c.id}</Link>
                            <span className="text-gray-500">{c.title || ''}</span>
                            {c.status && <StatusChip status={c.status} />}
                          </p>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* Acknowledgment audit */}
                  <Section icon={CheckCircle2} title={`Acknowledgment audit (${ackRows.length})`}>
                    <div className="border border-gray-100 rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr className="text-[10px] font-bold uppercase text-gray-400">
                          <th className="text-left px-3 py-2">Recipient</th><th className="text-left px-3 py-2">Unit</th><th className="text-left px-3 py-2">Notified</th><th className="text-left px-3 py-2">Acknowledged</th><th className="text-left px-3 py-2">SLA target</th><th className="text-left px-3 py-2">Actual</th><th className="text-left px-3 py-2">Result</th></tr></thead>
                        <tbody>
                          {ackRows.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-gray-400 text-xs">No acknowledgment records on the timeline.</td></tr>}
                          {ackRows.map(r => (
                            <tr key={r.id} className="border-t border-gray-50">
                              <td className="px-3 py-2 text-gray-800 font-semibold">{r.name}{r.offDuty && <span className="ml-1.5 text-[9px] font-bold text-gray-400 uppercase">off-duty</span>}</td>
                              <td className="px-3 py-2 text-gray-600">{r.unit}</td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{fmt(r.notifiedAt)}</td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.ackAt ? fmt(r.ackAt) : <span className="text-amber-600 font-bold text-xs">Pending</span>}</td>
                              <td className="px-3 py-2 text-gray-600">{r.slaTarget != null ? `${r.slaTarget}m` : '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{r.actual != null ? fmtDur(r.actual) : '—'}</td>
                              <td className="px-3 py-2"><AckResult r={r} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>

                  {/* Operations log */}
                  <Section icon={ClipboardList} title="Operations log">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {OPS_GROUPS.map(g => (
                        <div key={g.key} className={`flex items-center gap-2 rounded-lg border p-2.5 ${opsCounts[g.key] ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50/50'}`}>
                          <g.icon className={`w-4 h-4 ${opsCounts[g.key] ? 'text-indigo-500' : 'text-gray-300'}`} />
                          <div><p className={`text-sm font-black ${opsCounts[g.key] ? 'text-gray-800' : 'text-gray-300'}`}>{opsCounts[g.key] || 0}</p><p className="text-[9px] font-bold uppercase text-gray-400">{g.label}</p></div>
                        </div>
                      ))}
                    </div>
                  </Section>

                  {/* Communications */}
                  {reportComms && (
                    <Section icon={PhoneCall} title="Communications">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <Kpi label="Messages" value={reportComms.messages} tone="text-gray-900" />
                        <Kpi label="Calls" value={reportComms.calls?.total || 0} sub={reportComms.calls?.total ? `${fmtDur(reportComms.calls.total_minutes)} talk time` : undefined} tone="text-indigo-600" />
                        <Kpi label="Missed / declined" value={reportComms.calls?.missed || 0} tone={reportComms.calls?.missed ? 'text-rose-600' : 'text-gray-300'} />
                        <Kpi label="Longest silence" value={reportComms.longest_gap_minutes == null ? '—' : fmtDur(reportComms.longest_gap_minutes)} sub={reportComms.longest_gap_start ? `from ${fmt(reportComms.longest_gap_start)}` : undefined} tone={reportComms.longest_gap_minutes > 60 ? 'text-amber-600' : 'text-gray-700'} />
                      </div>
                      {(reportComms.hourly || []).length > 0 && (
                        <div>
                          <div className="flex items-end gap-[2px] h-10">
                            {reportComms.hourly.map(h => {
                              const max = Math.max(...reportComms.hourly.map(x => x.count));
                              return <div key={h.hour} title={`${fmt(h.hour)}: ${h.count} message(s)`} className="flex-1 min-w-0 bg-indigo-400 rounded-t-sm" style={{ height: `${Math.max(10, (h.count / max) * 100)}%` }} />;
                            })}
                          </div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-300 mt-1">Message activity per hour</p>
                        </div>
                      )}
                    </Section>
                  )}

                  {/* Readiness checks (admin-only source; hidden when unavailable) */}
                  {readinessChecks && readinessChecks.length > 0 && (
                    <Section icon={ShieldCheck} title={`Readiness checks (${readinessChecks.length})`}>
                      <div className="border border-gray-100 rounded-lg overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50"><tr className="text-[10px] font-bold uppercase text-gray-400">
                            <th className="text-left px-3 py-2">Sent</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Window</th><th className="text-left px-3 py-2">Responded</th><th className="text-left px-3 py-2">In duty</th><th className="text-left px-3 py-2">Readiness</th></tr></thead>
                          <tbody>
                            {readinessChecks.map((c, i) => {
                              const stats = c.stats || {};
                              return (
                                <tr key={c.id || i} className="border-t border-gray-50">
                                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmt(c.created_at)}</td>
                                  <td className="px-3 py-2"><StatusChip status={c.status} /></td>
                                  <td className="px-3 py-2 text-gray-600">{c.response_window_minutes != null ? `${c.response_window_minutes}m` : '—'}</td>
                                  <td className="px-3 py-2 text-gray-600">{stats.responded ?? 0}/{stats.total ?? 0}</td>
                                  <td className="px-3 py-2 text-gray-600">{stats.in_duty ?? 0}</td>
                                  <td className="px-3 py-2 font-bold text-gray-800">{c.readiness_percentage != null ? `${c.readiness_percentage}%` : '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Section>
                  )}

                  {/* Strengths & gaps */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
                      <p className="text-sm font-bold text-emerald-700 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Strengths</p>
                      <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                        {strengths.length === 0 && <li className="text-gray-400 list-none">No strengths captured yet</li>}
                        {strengths.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                    <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-3">
                      <p className="text-sm font-bold text-rose-700 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Gaps</p>
                      <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                        {gaps.length === 0 && <li className="text-gray-400 list-none">No gaps identified</li>}
                        {gaps.map((g, i) => <li key={i}>{g}</li>)}
                      </ul>
                    </div>
                  </div>

                  {/* Corrective actions */}
                  <Section icon={ClipboardList} title={`Corrective actions (${recActions.length})`}>
                    <div className="border border-gray-100 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr className="text-[10px] font-bold uppercase text-gray-400"><th className="text-left px-3 py-2">Action</th><th className="text-left px-3 py-2">Owner</th><th className="text-left px-3 py-2">Priority</th><th className="text-left px-3 py-2">Due</th><th className="text-left px-3 py-2">Status</th></tr></thead>
                        <tbody>
                          {recActions.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-gray-400 text-xs">{sourceErrors.includes('actions') ? 'Corrective actions are not available to this session.' : 'No corrective actions.'}</td></tr>}
                          {recActions.map(a => (
                            <tr key={a.id} className="border-t border-gray-50">
                              <td className="px-3 py-2 text-gray-800">{a.title}</td>
                              <td className="px-3 py-2 text-gray-600">{a.owner_name || '—'}</td>
                              <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full capitalize ${a.priority === 'high' ? 'bg-rose-50 text-rose-700' : a.priority === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{a.priority}</span></td>
                              <td className="px-3 py-2 text-gray-600">{fmtDay(a.due_date)}</td>
                              <td className="px-3 py-2"><span className="text-[11px] font-bold capitalize">{String(a.status || '').replace(/_/g, ' ')}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>

                  <p className="text-[10px] text-gray-400 border-t border-gray-100 pt-3">Generated {new Date().toLocaleString()} · DisasterMS Reports Center · {rec.id}</p>
                </div>
              )}
            </div>
          )}

          {/* ── SLA & PERFORMANCE ────────────────────────────────────────── */}
          {tab === 'perf' && (
            <div className="space-y-4" data-testid="report-performance">
              {perf.error && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">Performance analytics are not available to this session.</p>}
              {!perf.data && !perf.error && <div className="p-12 text-center text-gray-400 bg-white border border-gray-200 rounded-xl">Computing SLA compliance…</div>}
              {perf.data && (
                <>
                  {/* Benchmark targets (admin-editable) */}
                  {canManageCorrectiveActions && benchmarks && (
                    <div className="flex justify-end">
                      <button onClick={() => { setBenchDraft(benchmarks); setBenchOpen(v => !v); }} data-testid="reports-benchmarks-toggle"
                        className={`flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-lg border transition-colors ${benchOpen ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                        <Settings className="w-3.5 h-3.5" />Targets
                      </button>
                    </div>
                  )}
                  {benchOpen && benchmarks && (
                    <div className="bg-white border border-indigo-100 rounded-xl p-4" data-testid="reports-benchmarks-editor">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Benchmark targets — every rate below renders against these</p>
                      <div className="flex flex-wrap gap-4 items-end">
                        {[
                          ['mtta_minutes', 'MTTA ≤ (min)'], ['mttr_minutes', 'MTTR ≤ (min)'],
                          ['ack_sla_target', 'Ack SLA ≥ (%)'], ['verification_sla_target', 'Verification ≥ (%)'],
                          ['action_sla_target', 'Actions ≥ (%)'], ['duty_response_target', 'Duty response ≥ (%)'],
                          ['adherence_target', 'Plan adherence ≥ (%)'],
                        ].map(([key, label]) => (
                          <label key={key} className="text-[10px] font-bold uppercase text-gray-400">
                            {label}
                            <input type="number" min="0" value={benchDraft[key] ?? ''} onChange={e => setBenchDraft(d => ({ ...d, [key]: e.target.value }))}
                              className="block w-24 mt-1 text-sm font-semibold border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900" />
                          </label>
                        ))}
                        <button onClick={saveBenchmarks} data-testid="reports-benchmarks-save" className="text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg">Save targets</button>
                      </div>
                    </div>
                  )}

                  {/* SLA compliance per card family */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      ['acknowledgment', 'Notification → Acknowledgment', Megaphone, 'ack_sla_target'],
                      ['verification', 'Verification', ShieldCheck, 'verification_sla_target'],
                      ['response_action', 'Response actions · T+ cards', Target, 'action_sla_target'],
                    ].map(([key, label, Icon, benchKey]) => {
                      const s = perf.data.sla?.[key] || {};
                      const target = benchmarks?.[benchKey];
                      const delta = s.rate != null && target != null ? Math.round((s.rate - target) * 10) / 10 : null;
                      return (
                        <div key={key} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`sla-${key}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400"><Icon className="w-3.5 h-3.5" />{label}</p>
                            <p className={`text-3xl font-black ${rateTone(s.rate)}`}>{s.rate == null ? '—' : `${s.rate}%`}</p>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1">{s.met || 0}/{s.tracked || 0} SLAs met · {s.pending || 0} pending · {s.cards || 0} cards</p>
                          {target != null && (
                            <p className="mt-1">
                              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${delta == null ? 'bg-gray-100 text-gray-400' : delta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                {delta == null ? `target ≥ ${target}%` : delta >= 0 ? `✓ ${delta} pts above ${target}% target` : `${Math.abs(delta)} pts below ${target}% target`}
                              </span>
                            </p>
                          )}
                          <MiniTrend weeks={s.weekly} color={s.rate == null ? 'bg-gray-200' : s.rate >= 80 ? 'bg-emerald-400' : s.rate >= 50 ? 'bg-amber-400' : 'bg-rose-400'} />
                          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-300 mt-1">12-week SLA compliance trend</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Escalation analytics */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="escalation-analytics">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Escalation analytics</p>
                      <div className="flex items-end gap-6 mb-3">
                        <div><p className={`text-3xl font-black ${perf.data.escalation?.rate > 25 ? 'text-rose-600' : 'text-gray-900'}`}>{perf.data.escalation?.rate == null ? '—' : `${perf.data.escalation.rate}%`}</p><p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Escalation rate</p></div>
                        <div><p className="text-3xl font-black text-gray-700">{perf.data.escalation?.escalated || 0}</p><p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">of {perf.data.escalation?.acks || 0} acknowledgments</p></div>
                      </div>
                      {(perf.data.escalation?.top_units || []).length > 0 && (
                        <>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Most-escalating units</p>
                          {(perf.data.escalation.top_units || []).slice(0, 5).map(u => (
                            <div key={u.name} className="flex items-center justify-between text-sm border-t border-gray-50 py-1">
                              <span className="text-gray-700">{u.name} <span className="text-[10px] text-gray-400">({u.kind})</span></span>
                              <span className="font-bold text-rose-600">{u.escalated}/{u.acks} escalated</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                    <BarChart title="Acknowledgments by escalation level (L0 = direct)" data={Object.entries(perf.data.escalation?.by_level || {}).map(([level, value]) => ({ label: `Level ${level}`, value }))} />
                  </div>

                  {/* Scoreboards */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="responder-scoreboard">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 p-4 pb-2">Responder scoreboard</p>
                      <table className="w-full text-sm">
                        <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50"><th className="text-left px-4 py-2">Responder</th><th className="text-right px-2 py-2">Acks</th><th className="text-right px-2 py-2">SLA %</th><th className="text-right px-2 py-2">Median</th><th className="text-right px-4 py-2">Escalated</th></tr></thead>
                        <tbody>
                          {(perf.data.responders || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400 text-xs">No acknowledgment activity in this view.</td></tr>}
                          {(perf.data.responders || []).map(r => (
                            <tr key={r.name} className="border-t border-gray-50">
                              <td className="px-4 py-1.5 font-semibold text-gray-800">{r.name}{r.off_duty > 0 && <span className="ml-1.5 text-[9px] font-bold text-gray-400 uppercase">{r.off_duty}× off-duty</span>}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{r.acks}</td>
                              <td className={`px-2 py-1.5 text-right font-bold ${rateTone(r.rate)}`}>{r.rate == null ? '—' : `${r.rate}%`}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{fmtDur(r.median_minutes)}</td>
                              <td className="px-4 py-1.5 text-right text-gray-600">{r.escalation_rate == null ? '—' : `${r.escalation_rate}%`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="unit-league">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 p-4 pb-2">Department / team / crew league</p>
                      <table className="w-full text-sm">
                        <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50"><th className="text-left px-4 py-2">Unit</th><th className="text-right px-2 py-2">Acks</th><th className="text-right px-2 py-2">SLA %</th><th className="text-right px-2 py-2">Median</th><th className="text-right px-4 py-2">Escalated</th></tr></thead>
                        <tbody>
                          {(perf.data.units || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400 text-xs">No unit attribution on acknowledgments in this view yet — unit names appear once escalation-rule cards carry crew/team/department targets.</td></tr>}
                          {(perf.data.units || []).map(u => (
                            <tr key={u.name} className="border-t border-gray-50">
                              <td className="px-4 py-1.5 font-semibold text-gray-800">{u.name} <span className="text-[10px] text-gray-400">({u.kind})</span></td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{u.acks}</td>
                              <td className={`px-2 py-1.5 text-right font-bold ${rateTone(u.rate)}`}>{u.rate == null ? '—' : `${u.rate}%`}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{fmtDur(u.median_minutes)}</td>
                              <td className="px-4 py-1.5 text-right text-gray-600">{u.escalation_rate == null ? '—' : `${u.escalation_rate}%`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Duty-check program */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="duty-program">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Duty-check &amp; readiness program</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <Kpi label="Checks sent" value={perf.data.duty_checks?.checks || 0} tone="text-gray-900" icon={ShieldCheck} />
                      <Kpi label="Recipients" value={perf.data.duty_checks?.recipients || 0} tone="text-gray-700" icon={Users} />
                      <Kpi label="Response rate" value={perf.data.duty_checks?.response_rate == null ? '—' : `${perf.data.duty_checks.response_rate}%`}
                        tone={benchmarks && perf.data.duty_checks?.response_rate != null ? (perf.data.duty_checks.response_rate >= benchmarks.duty_response_target ? 'text-emerald-600' : 'text-rose-600') : rateTone(perf.data.duty_checks?.response_rate)}
                        sub={benchmarks ? `target ≥ ${benchmarks.duty_response_target}%` : undefined} icon={Activity} />
                      <Kpi label="Reported in duty" value={perf.data.duty_checks?.in_duty || 0} tone="text-emerald-600" icon={CheckCircle2} />
                    </div>
                    <MiniTrend weeks={perf.data.duty_checks?.weekly} color="bg-sky-400" />
                    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-300 mt-1 mb-3">12-week response-rate trend</p>
                    {(perf.data.duty_checks?.least_responsive || []).length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Least responsive</p>
                        <div className="flex flex-wrap gap-1.5">
                          {perf.data.duty_checks.least_responsive.map(u => (
                            <span key={u.name} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-100">{u.name} · {u.unanswered} unanswered</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SCENARIO ADHERENCE ───────────────────────────────────────── */}
          {tab === 'adherence' && (
            <div className="space-y-4" data-testid="report-adherence">
              {adherence.error && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">Adherence analytics are not available to this session.</p>}
              {!adherence.data && !adherence.error && <div className="p-12 text-center text-gray-400 bg-white border border-gray-200 rounded-xl">Scoring plan adherence…</div>}
              {adherence.data && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                    <Kpi label="Real incidents" value={adherence.data.funnel?.real ?? 0} tone="text-rose-600" icon={Siren} />
                    <Kpi label="Matched a scenario" value={adherence.data.funnel?.matched ?? 0} sub={adherence.data.funnel?.match_rate == null ? undefined : `${adherence.data.funnel.match_rate}% match rate`} tone="text-indigo-600" icon={Target} />
                    <Kpi label="Plans activated" value={adherence.data.funnel?.activated ?? 0} sub={adherence.data.funnel?.activation_rate == null ? undefined : `${adherence.data.funnel.activation_rate}% of matched`} tone="text-emerald-600" icon={Zap} />
                    <Kpi label="Auto vs manual" value={`${adherence.data.funnel?.auto_activated ?? 0}/${adherence.data.funnel?.manual_activated ?? 0}`} sub="auto / manual" tone="text-sky-600" icon={Activity} />
                    <Kpi label="Auto cancelled" value={adherence.data.funnel?.auto_cancelled ?? 0} sub={(adherence.data.funnel?.top_cancellers || [])[0] ? `mostly ${adherence.data.funnel.top_cancellers[0].name}` : undefined} tone="text-amber-600" icon={XCircle} />
                    <Kpi label="Median plan adherence" value={adherence.data.avg_adherence == null ? '—' : `${adherence.data.avg_adherence}%`} sub="SLA-based, not delivery" tone={rateTone(adherence.data.avg_adherence)} icon={Gauge} />
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="adherence-plans">
                    <div className="p-4 pb-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Activated response plans — SLA adherence &amp; T+ drift</p>
                      <p className="text-[11px] text-gray-400">Adherence counts a plan card only when its SLA was met — delivery to staffed roles alone scores zero. Drift is the median gap between a card's scheduled T+ time and its actual dispatch.</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50"><th className="text-left px-4 py-2">Incident</th><th className="text-left px-2 py-2">Scenario</th><th className="text-left px-2 py-2">Activated</th><th className="text-left px-2 py-2">Mode</th><th className="text-right px-2 py-2">Cards</th><th className="text-right px-2 py-2">SLA met</th><th className="text-right px-2 py-2">Adherence</th><th className="text-right px-4 py-2">Median drift</th></tr></thead>
                        <tbody>
                          {(adherence.data.plans || []).length === 0 && <tr><td colSpan={8} className="p-6 text-center text-gray-400 text-xs">No activated response plans in this view.</td></tr>}
                          {(adherence.data.plans || []).map(p => (
                            <tr key={p.incident_id} className="border-t border-gray-50 hover:bg-gray-50">
                              <td className="px-4 py-1.5 whitespace-nowrap"><Link to={`/incident-details/${p.incident_id}`} className="text-indigo-600 hover:underline font-semibold">{p.incident_id}</Link></td>
                              <td className="px-2 py-1.5 text-gray-600 max-w-[180px] truncate">{p.scenario || '—'}</td>
                              <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{fmt(p.activated_at)}</td>
                              <td className="px-2 py-1.5 text-gray-600 capitalize">{p.activation_mode || '—'}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{p.cards}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{p.sla_met}/{p.sla_tracked}</td>
                              <td className={`px-2 py-1.5 text-right font-black ${rateTone(p.adherence)}`}>{p.adherence == null ? '—' : `${p.adherence}%`}</td>
                              <td className="px-4 py-1.5 text-right text-gray-600">{p.avg_drift_minutes == null ? '—' : `${p.avg_drift_minutes > 0 ? '+' : ''}${p.avg_drift_minutes}m`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="coverage-gaps">
                    <div className="p-4 pb-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Coverage gaps — write these scenarios next</p>
                      <p className="text-[11px] text-gray-400">Real incidents whose classification matched no scenario in the library, ranked by how often they keep happening.</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50"><th className="text-left px-4 py-2">Incident type</th><th className="text-left px-2 py-2">Event</th><th className="text-left px-2 py-2">Trigger</th><th className="text-right px-4 py-2">Unplanned incidents</th></tr></thead>
                      <tbody>
                        {(adherence.data.coverage_gaps || []).length === 0 && <tr><td colSpan={4} className="p-6 text-center text-gray-400 text-xs">Every real incident in this view matched a scenario — full coverage.</td></tr>}
                        {(adherence.data.coverage_gaps || []).map((g, i) => (
                          <tr key={i} className="border-t border-gray-50">
                            <td className="px-4 py-1.5 font-semibold text-gray-800">{g.incident_type}</td>
                            <td className="px-2 py-1.5 text-gray-600">{g.event}</td>
                            <td className="px-2 py-1.5 text-gray-600">{g.trigger}</td>
                            <td className="px-4 py-1.5 text-right"><span className={`font-black ${g.count >= 10 ? 'text-rose-600' : 'text-amber-600'}`}>{g.count}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Training transfer: drills vs real per scenario */}
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="training-transfer">
                    <div className="p-4 pb-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Training transfer — drills vs real, per scenario</p>
                      <p className="text-[11px] text-gray-400">Does exercising a scenario improve the live response? Ack-SLA compliance and median response, side by side.</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50">
                          <th className="text-left px-4 py-2">Scenario</th>
                          <th className="text-right px-2 py-2">Drills</th><th className="text-right px-2 py-2">Drill SLA %</th><th className="text-right px-2 py-2">Drill median</th>
                          <th className="text-right px-2 py-2">Real</th><th className="text-right px-2 py-2">Real SLA %</th><th className="text-right px-2 py-2">Real median</th>
                          <th className="text-right px-4 py-2">Transfer</th></tr></thead>
                        <tbody>
                          {(adherence.data.training_transfer || []).length === 0 && <tr><td colSpan={8} className="p-6 text-center text-gray-400 text-xs">No scenario-linked incidents in this view.</td></tr>}
                          {(adherence.data.training_transfer || []).map(t => {
                            const gap = t.drills.rate != null && t.real.rate != null ? Math.round((t.real.rate - t.drills.rate) * 10) / 10 : null;
                            return (
                              <tr key={t.scenario_id} className="border-t border-gray-50">
                                <td className="px-4 py-1.5 font-semibold text-gray-800 max-w-[180px] truncate">{t.scenario}</td>
                                <td className="px-2 py-1.5 text-right text-gray-600">{t.drills.incidents}</td>
                                <td className={`px-2 py-1.5 text-right font-bold ${rateTone(t.drills.rate)}`}>{t.drills.rate == null ? '—' : `${t.drills.rate}%`}</td>
                                <td className="px-2 py-1.5 text-right text-gray-600">{fmtDur(t.drills.median_minutes)}</td>
                                <td className="px-2 py-1.5 text-right text-gray-600">{t.real.incidents}</td>
                                <td className={`px-2 py-1.5 text-right font-bold ${rateTone(t.real.rate)}`}>{t.real.rate == null ? '—' : `${t.real.rate}%`}</td>
                                <td className="px-2 py-1.5 text-right text-gray-600">{fmtDur(t.real.median_minutes)}</td>
                                <td className="px-4 py-1.5 text-right">
                                  {gap == null ? <span className="text-gray-300 text-xs">not comparable</span>
                                    : <span className={`text-[11px] font-black ${gap >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{gap >= 0 ? `+${gap} pts live` : `${gap} pts live`}</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Exercise program compliance */}
                  {program && (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="program-compliance">
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 p-4 pb-2">Drill cadence — last 4 quarters</p>
                          <table className="w-full text-sm">
                            <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50">
                              <th className="text-left px-4 py-2">Quarter</th>
                              {program.cadence.classifications.map(c => <th key={c} className="text-right px-3 py-2">{c}</th>)}
                            </tr></thead>
                            <tbody>
                              {program.cadence.matrix.map(row => (
                                <tr key={row.quarter} className="border-t border-gray-50">
                                  <td className="px-4 py-1.5 font-semibold text-gray-800">{row.quarter}</td>
                                  {program.cadence.classifications.map(c => (
                                    <td key={c} className={`px-3 py-1.5 text-right font-bold ${row[c] ? 'text-gray-800' : 'text-gray-300'}`}>{row[c] || 0}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 p-4 pb-2">Never exercised — real incidents, zero drills</p>
                          <table className="w-full text-sm">
                            <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50"><th className="text-left px-4 py-2">Type</th><th className="text-left px-2 py-2">Location</th><th className="text-right px-4 py-2">Real incidents</th></tr></thead>
                            <tbody>
                              {(program.never_exercised || []).length === 0 && <tr><td colSpan={3} className="p-6 text-center text-gray-400 text-xs">Every incident-generating type + location has been drilled.</td></tr>}
                              {(program.never_exercised || []).map((n, i) => (
                                <tr key={i} className="border-t border-gray-50">
                                  <td className="px-4 py-1.5 font-semibold text-gray-800">{n.incident_type}{!n.type_ever_drilled && <span className="ml-1.5 text-[9px] font-black text-rose-500 uppercase">type never drilled</span>}</td>
                                  <td className="px-2 py-1.5 text-gray-600">{n.location}</td>
                                  <td className="px-4 py-1.5 text-right font-black text-amber-600">{n.real_incidents}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 p-4 pb-2">Scenario staleness — time since last drill</p>
                          <table className="w-full text-sm">
                            <tbody>
                              {(program.staleness || []).map(s => (
                                <tr key={s.scenario} className="border-t border-gray-50">
                                  <td className="px-4 py-1.5 font-semibold text-gray-800 max-w-[200px] truncate">{s.scenario} <span className="text-[10px] text-gray-400">({s.status})</span></td>
                                  <td className="px-4 py-1.5 text-right">
                                    {s.days_since == null
                                      ? <span className="text-[11px] font-black text-rose-600">never drilled</span>
                                      : <span className={`text-[11px] font-black ${s.days_since > 90 ? 'text-rose-600' : s.days_since > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>{s.days_since}d ago</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="space-y-4">
                          <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Objective achievement</p>
                            <div className="flex items-end gap-5">
                              <div><p className={`text-3xl font-black ${rateTone(program.objectives?.achievement_rate)}`}>{program.objectives?.achievement_rate == null ? '—' : `${program.objectives.achievement_rate}%`}</p><p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">achieved</p></div>
                              <p className="text-[11px] text-gray-500">{program.objectives?.scored || 0} of {program.objectives?.drills_with_objective || 0} drills with an objective have been scored{program.objectives?.by_score ? ` (${Object.entries(program.objectives.by_score).map(([k, v]) => `${v} ${k}`).join(', ') || 'none yet'})` : ''}. Score drills in their AAR.</p>
                            </div>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Observations → corrective actions</p>
                            <div className="flex items-end gap-5">
                              <div><p className="text-3xl font-black text-gray-900">{program.observations?.conversion_rate == null ? '—' : `${program.observations.conversion_rate}%`}</p><p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">converted</p></div>
                              <p className="text-[11px] text-gray-500">{program.observations?.converted_to_actions || 0} of {program.observations?.total || 0} observations became corrective actions · median closure {program.observations?.median_closure_days == null ? '—' : `${program.observations.median_closure_days}d`}.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── OPERATIONS (resources, action cards, comms) ──────────────── */}
          {tab === 'ops' && (
            <div className="space-y-4" data-testid="report-operations-tab">
              {opsData.error && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">Operations analytics are not available to this session.</p>}
              {!opsData.data && !opsData.error && <div className="p-12 text-center text-gray-400 bg-white border border-gray-200 rounded-xl">Aggregating operations data…</div>}
              {opsData.data && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
                    <Kpi label="Resource requests" value={opsData.data.resources?.requests || 0} tone="text-gray-900" icon={Package} />
                    <Kpi label="Put to use" value={opsData.data.resources?.used || 0} sub={opsData.data.resources?.usage_rate == null ? undefined : `${opsData.data.resources.usage_rate}% usage rate`} tone="text-emerald-600" icon={CheckCircle2} />
                    <Kpi label="Median to first use" value={opsData.data.resources?.median_minutes_to_first_use == null ? '—' : fmtDur(opsData.data.resources.median_minutes_to_first_use)} tone="text-sky-600" icon={Clock} />
                    <Kpi label="Action card runs" value={(opsData.data.action_cards || []).reduce((s, a) => s + a.runs, 0)} tone="text-indigo-600" icon={Target} />
                    <Kpi label="Calls" value={opsData.data.calls?.total || 0} tone="text-gray-700" icon={PhoneCall} />
                    <Kpi label="Missed / declined" value={opsData.data.calls?.missed || 0} tone={opsData.data.calls?.missed ? 'text-rose-600' : 'text-gray-300'} icon={XCircle} />
                    <Kpi label="Median call" value={opsData.data.calls?.median_minutes == null ? '—' : fmtDur(opsData.data.calls.median_minutes)} tone="text-sky-600" icon={PhoneCall} />
                    <Kpi label="Talk time" value={fmtDur(opsData.data.calls?.total_minutes)} tone="text-gray-700" icon={Activity} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <BarChart title="Resource requests by category" data={(opsData.data.resources?.by_category || []).map(b => ({ label: b.category, value: b.requests }))} />
                    <BarChart title="Resource requests by incident type" data={(opsData.data.resources?.by_incident_type || []).map(b => ({ label: b.incident_type, value: b.requests }))} />
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="action-card-execution">
                    <div className="p-4 pb-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Action-card execution</p>
                      <p className="text-[11px] text-gray-400">Per card: how often it ran, how much of its checklist actually got done, whether evidence was attached, and its SLA record.</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50">
                          <th className="text-left px-4 py-2">Card</th><th className="text-right px-2 py-2">Runs</th><th className="text-right px-2 py-2">Tasks done</th><th className="text-right px-2 py-2">Completion</th><th className="text-right px-2 py-2">Evidence</th><th className="text-right px-4 py-2">SLA</th></tr></thead>
                        <tbody>
                          {(opsData.data.action_cards || []).length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-400 text-xs">No response-action cards in this view.</td></tr>}
                          {(opsData.data.action_cards || []).map(a => (
                            <tr key={a.title} className="border-t border-gray-50">
                              <td className="px-4 py-1.5 font-semibold text-gray-800 max-w-[220px] truncate">{a.title}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{a.runs}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{a.tasks_done}/{a.tasks}</td>
                              <td className={`px-2 py-1.5 text-right font-bold ${rateTone(a.completion_rate)}`}>{a.completion_rate == null ? '—' : `${a.completion_rate}%`}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{a.evidence_rate == null ? '—' : `${a.evidence_rate}%`}</td>
                              <td className={`px-4 py-1.5 text-right font-bold ${rateTone(a.sla_rate)}`}>{a.sla_rate == null ? '—' : `${a.sla_rate}%`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── ACKNOWLEDGMENT AUDIT (cross-incident, server-paginated) ──── */}
          {tab === 'acks' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="report-ack-audit">
              <div className="flex items-center justify-between p-3 border-b border-gray-100 flex-wrap gap-2">
                <div>
                  <p className="text-sm font-bold text-gray-800">Acknowledgment audit ({ackAudit.total})</p>
                  <p className="text-[11px] text-gray-400">Every alert recipient across {SCOPES.find(s => s.id === scope)?.label.toLowerCase() || 'all records'} — who was notified, when they acknowledged, and SLA outcome.</p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={ackSla} onChange={e => setAckSla(e.target.value)} data-testid="ack-sla-filter" className="text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-900">
                    <option value="all">All outcomes</option>
                    <option value="met">SLA met</option>
                    <option value="missed">SLA missed</option>
                    <option value="pending">Awaiting response</option>
                  </select>
                  <button onClick={() => downloadCsv('acknowledgment_audit.csv', ackAudit.items.map(r => ({
                    Incident: r.incidentId, Title: r.incidentTitle || '—', Kind: r.incidentIsExercise ? 'Drill' : 'Real',
                    Recipient: r.name, Unit: r.unit, Notified: fmt(r.notifiedAt), Acknowledged: r.ackAt ? fmt(r.ackAt) : 'Pending',
                    'SLA target': r.slaTarget != null ? `${r.slaTarget}m` : '—', Actual: r.actual != null ? fmtDur(r.actual) : '—',
                    Result: r.met === true ? 'SLA met' : r.met === false ? 'SLA missed' : r.ackAt ? 'No SLA' : 'Awaiting',
                  })))} className="flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" />Export CSV</button>
                </div>
              </div>
              {ackAudit.error && <p className="m-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">The acknowledgment audit is not available to this session.</p>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    <th className="text-left px-4 py-2.5">Incident</th><th className="text-left px-4 py-2.5">Recipient</th><th className="text-left px-4 py-2.5">Unit</th><th className="text-left px-4 py-2.5">Notified</th><th className="text-left px-4 py-2.5">Acknowledged</th><th className="text-left px-4 py-2.5">SLA target</th><th className="text-left px-4 py-2.5">Actual</th><th className="text-left px-4 py-2.5">Result</th></tr></thead>
                  <tbody>
                    {!ackAudit.error && !ackAudit.loading && ackAudit.items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-gray-400">No acknowledgment records match the filters.</td></tr>}
                    {ackAudit.items.map(r => (
                      <tr key={r.id} className="border-t border-gray-50 hover:bg-gray-50">
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <Link to={`/incident-details/${r.incidentId}`} className="text-indigo-600 hover:underline font-semibold">{r.incidentId}</Link>
                          <span className={`ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${r.incidentIsExercise ? 'bg-indigo-50 text-indigo-700' : 'bg-rose-50 text-rose-700'}`}>{r.incidentIsExercise ? 'Drill' : 'Real'}</span>
                          {r.incidentIsSos && <span className="ml-1 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-red-600 text-white">SOS</span>}
                          {r.incidentTitle && <p className="text-[11px] text-gray-400 truncate max-w-[220px]">{r.incidentTitle}</p>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-800 font-semibold">{r.name}{r.offDuty && <span className="ml-1.5 text-[9px] font-bold text-gray-400 uppercase">off-duty</span>}</td>
                        <td className="px-4 py-2.5 text-gray-600">{r.unit}</td>
                        <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{fmt(r.notifiedAt)}</td>
                        <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{r.ackAt ? fmt(r.ackAt) : <span className="text-amber-600 font-bold text-xs">Pending</span>}</td>
                        <td className="px-4 py-2.5 text-gray-600">{r.slaTarget != null ? `${r.slaTarget}m` : '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{r.actual != null ? fmtDur(r.actual) : '—'}</td>
                        <td className="px-4 py-2.5"><AckResult r={r} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">{ackAudit.items.length} of {ackAudit.total} shown</p>
                {ackAudit.items.length < ackAudit.total && (
                  <button onClick={() => loadAckAudit(true)} disabled={ackAudit.loading} data-testid="ack-load-more" className="text-xs font-bold text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-md disabled:opacity-50">
                    {ackAudit.loading ? 'Loading…' : 'Load 50 more'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── CORRECTIVE ACTIONS REGISTER (scope-aware) ────────────────── */}
          {tab === 'ca' && (
            <div className="space-y-4" data-testid="report-ca-register">
              {caUnavailable && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">Corrective actions are not available to this read-only session.</p>}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label="Total" value={caUnavailable ? '—' : caStats.total} tone="text-gray-900" icon={ClipboardCheck} />
                <Kpi label="Open" value={caUnavailable ? '—' : caStats.open} tone="text-slate-700" icon={Clock} />
                <Kpi label="In progress" value={caUnavailable ? '—' : caStats.in_progress} tone="text-amber-600" icon={Activity} />
                <Kpi label="Completed" value={caUnavailable ? '—' : caStats.completed} tone="text-emerald-600" icon={CheckCircle2} />
                <Kpi label="Overdue" value={caUnavailable ? '—' : caStats.overdue} tone="text-rose-600" icon={AlertTriangle} />
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-3 border-b border-gray-100 flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-bold text-gray-800">Corrective actions — {SCOPES.find(s => s.id === scope)?.label || 'All records'} ({caRows.length})</p>
                    <p className="text-[11px] text-gray-400">Continuous improvement tracking across real incidents, exercises &amp; drills. Use the search box above to filter.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={caStatusF} onChange={e => setCaStatusF(e.target.value)} data-testid="reports-ca-status-filter" className="text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-900">
                      <option value="all">All statuses</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button onClick={() => setCaOverdueOnly(v => !v)} data-testid="reports-ca-overdue-toggle"
                      className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-md border ${caOverdueOnly ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
                      <AlertTriangle className="w-3.5 h-3.5" />Overdue only
                    </button>
                    <button onClick={() => downloadCsv(`corrective_actions_${scope}.csv`, caRows.map(a => ({
                      Action: a.title, Priority: a.priority, Owner: a.owner_name || '—', Record: a.incident_label || a.incident_id,
                      Kind: a.incident ? (isDrill(a.incident) ? 'Drill' : 'Real') : '—', Due: fmt(a.due_date), Status: a.status,
                    })))} className="flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" />Export CSV</button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="text-left px-4 py-2.5">Action</th><th className="text-left px-4 py-2.5">Owner</th><th className="text-left px-4 py-2.5">Record</th><th className="text-left px-4 py-2.5">Due</th><th className="text-left px-4 py-2.5">Status</th></tr></thead>
                    <tbody>
                      {caRows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-gray-400">{caUnavailable ? 'Corrective actions are not available to this session.' : 'No corrective actions match this scope. Actions raised from a Response Report appear here automatically.'}</td></tr>}
                      {caRows.map(a => {
                        const od = caIsOverdue(a);
                        return (
                          <tr key={a.id} className={`border-t border-gray-50 ${od ? 'bg-rose-50/40' : 'hover:bg-gray-50'}`} data-testid={`reports-ca-row-${a.id}`}>
                            <td className="px-4 py-2.5 min-w-[220px]">
                              <div className="flex items-center gap-2">
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${CA_PRIORITY_STYLE[a.priority] || CA_PRIORITY_STYLE.medium}`}>{(a.priority || 'medium').toUpperCase()}</span>
                                <p className="font-semibold text-gray-900">{a.title}</p>
                              </div>
                              {a.description && <p className="text-xs text-gray-500 truncate max-w-[320px] mt-0.5">{a.description}</p>}
                            </td>
                            <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{a.owner_name || '—'} <span className="text-[10px] text-gray-400">({a.owner_type})</span></td>
                            <td className="px-4 py-2.5 whitespace-nowrap">
                              <Link to={`/incident-details/${a.incident_id}`} className="text-indigo-600 hover:underline flex items-center gap-1 text-xs">{a.incident_label || a.incident_id}<ExternalLink className="w-3 h-3" /></Link>
                              {a.incident && <KindBadge rec={a.incident} />}
                            </td>
                            <td className={`px-4 py-2.5 text-xs whitespace-nowrap ${od ? 'text-rose-600 font-bold' : 'text-gray-600'}`}>{fmt(a.due_date)}</td>
                            <td className="px-4 py-2.5">
                              {canManageCorrectiveActions ? (
                                <select value={a.status} onChange={e => onCaStatusChange(a, e.target.value)} data-testid={`reports-ca-status-${a.id}`}
                                  className={`text-[11px] font-bold border rounded-md px-2 py-1 cursor-pointer ${CA_STATUS_STYLE[a.status] || CA_STATUS_STYLE.open}`}>
                                  <option value="open">Open</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                </select>
                              ) : (
                                <span className={`text-[11px] font-bold px-2 py-1 rounded-md border ${CA_STATUS_STYLE[a.status] || CA_STATUS_STYLE.open}`}>{String(a.status || 'open').replace(/_/g, ' ')}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── AUDIT LOG (web console + mobile app) ─────────────────────── */}
          {tab === 'audit' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="report-audit-log">
              <div className="flex items-center justify-between p-3 border-b border-gray-100 flex-wrap gap-2">
                <div>
                  <p className="text-sm font-bold text-gray-800">Full audit log ({auditLog.total})</p>
                  <p className="text-[11px] text-gray-400">Every audited action across the web console and the mobile app — mutations, corrections and policy changes, newest first.</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="inline-flex gap-1 bg-gray-100 rounded-lg p-0.5">
                    {[['all', 'All', Layers], ['web', 'Web', Globe], ['mobile', 'Mobile app', Smartphone]].map(([id, label, Icon]) => (
                      <button key={id} onClick={() => setAuditSource(id)} data-testid={`audit-source-${id}`}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${auditSource === id ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <Icon className="w-3.5 h-3.5" />{label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => downloadCsv('audit_log.csv', auditLog.items.map(e => ({
                    Time: fmt(e.created_at), Source: e.source || '—', Actor: e.actor_name, Action: e.action,
                    Detail: e.detail || '—', Record: e.incident_id || '—', Category: e.category,
                  })))} className="flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" />Export CSV</button>
                </div>
              </div>
              {auditLog.error && <p className="m-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">The audit log is not available to this session.</p>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    <th className="text-left px-4 py-2.5">Time</th><th className="text-left px-4 py-2.5">Source</th><th className="text-left px-4 py-2.5">Actor</th><th className="text-left px-4 py-2.5">Action</th><th className="text-left px-4 py-2.5">Detail</th><th className="text-left px-4 py-2.5">Record</th></tr></thead>
                  <tbody>
                    {!auditLog.error && !auditLog.loading && auditLog.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-400">No audit entries match the filters.</td></tr>}
                    {auditLog.items.map((e, i) => (
                      <tr key={e.id || i} className="border-t border-gray-50 hover:bg-gray-50 align-top">
                        <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{fmt(e.created_at)}</td>
                        <td className="px-4 py-2.5">
                          {e.source === 'mobile' ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700"><Smartphone className="w-3 h-3" />App</span>
                            : e.source === 'web' ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700"><Globe className="w-3 h-3" />Web</span>
                            : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-800 font-semibold whitespace-nowrap">{e.actor_name}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap"><span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{e.action}</span><p className="text-[10px] text-gray-400 mt-0.5 capitalize">{String(e.category || '').replace(/_/g, ' ')}</p></td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs max-w-[360px]"><span className="line-clamp-2 break-all">{e.detail || '—'}</span></td>
                        <td className="px-4 py-2.5 whitespace-nowrap">{e.incident_id ? <Link to={`/incident-details/${e.incident_id}`} className="text-indigo-600 hover:underline text-xs flex items-center gap-1">{e.incident_id}<ExternalLink className="w-3 h-3" /></Link> : <span className="text-gray-300 text-xs">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">{auditLog.items.length} of {auditLog.total} shown{auditLog.loading ? ' · loading…' : ''}</p>
                {auditLog.items.length < auditLog.total && (
                  <button onClick={() => loadAuditLog(true)} disabled={auditLog.loading} data-testid="audit-load-more" className="text-xs font-bold text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-md disabled:opacity-50">
                    {auditLog.loading ? 'Loading…' : 'Load 50 more'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── NOTIFICATION LOG ─────────────────────────────────────────── */}
          {tab === 'notifications' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="report-notifications">
              <div className="flex items-center justify-between p-3 border-b border-gray-100">
                <div>
                  <p className="text-sm font-bold text-gray-800">Notification log ({visibleNotifications.length})</p>
                  <p className="text-[11px] text-gray-400">Alerts delivered to this account. Early warnings live in ForeSight; per-incident delivery &amp; SLA audit lives in the Response Report tab.</p>
                </div>
                <button onClick={() => downloadCsv('notifications_log.csv', visibleNotifications.map(n => ({ Type: n.type, Title: n.title, Sent: fmt(n.created_at), Status: n.read ? 'Acknowledged' : 'Pending' })))} className="flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" />Export CSV</button>
              </div>
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50"><tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    <th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Title</th><th className="text-left px-4 py-2.5">Sent at</th><th className="text-left px-4 py-2.5">Response</th></tr></thead>
                  <tbody>
                    {visibleNotifications.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-gray-400">{sourceErrors.includes('notifications') ? 'Notification audit data is not available to this session.' : 'No notifications recorded.'}</td></tr>}
                    {visibleNotifications.map((n, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="px-4 py-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{n.type || 'INFO'}</span></td>
                        <td className="px-4 py-2.5 text-gray-700">{n.title}{n.subtitle ? <span className="text-gray-400"> · {n.subtitle}</span> : ''}</td>
                        <td className="px-4 py-2.5 text-gray-500">{fmt(n.created_at)}</td>
                        <td className="px-4 py-2.5">{n.read ? <span className="flex items-center gap-1 text-emerald-600 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5" />Acknowledged</span> : <span className="flex items-center gap-1 text-amber-600 text-xs font-bold"><AlertTriangle className="w-3.5 h-3.5" />Pending</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {showGuide && <ReportsGuide onClose={() => setShowGuide(false)} />}

      {showExtensive && rec && (
        <ExtensiveAnalysisModal
          onClose={() => setShowExtensive(false)}
          title={`Extensive Analysis — ${rec.id}`}
          incidentId={rec.id}
          reviewKind={isDrill(rec) ? 'aar' : 'pir'}
          defaultObjective={isDrill(rec)
            ? "Evaluate this exercise's end-to-end performance and produce a full, decision-ready After-Action Review."
            : "Evaluate this real incident's end-to-end response and produce a full, decision-ready Post-Incident Review."}
          defaultMaterial={extensiveMaterial}
        />
      )}
      </div>
    </div>
  );
};

export default ReportsCenter;
